import { Injectable, inject, isDevMode } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { tapResponse } from '@ngrx/component-store';
import { EventBusEvents } from '@siza-staff-care/event-bus-constants';
import { AppStateService } from '@siza-staff-care/shared/app-state';
import {
	LookupType,
	SelectOption,
	StaffSecurityEditDto,
	StaffSecurityEditItemDto,
	Types,
	obfuscate,
	tryDecode,
} from '@siza-staff-care/shared/data';
import { GenericEditStore } from '@siza-staff-care/shared/generic-edit';
import {
	EditFlagsValue,
	EditFlags_All,
	EditFlags_Create,
	EditFlags_Delete,
	EditFlags_Update,
	MenuBuilderService,
	securityPaths,
	securityPathsReverseLookup,
} from '@siza-staff-care/shared/global-services';
import { IStore } from '@siza-staff-care/shared/ui-components/store-registry';
import { TreeItem, assertDefined, buildTree, typedClone } from '@siza-staff-care/shared/utils';
import { GridApi } from 'ag-grid-community';
import { flatMap, isEqual, keyBy } from 'lodash-es';
import {
	EMPTY,
	Observable,
	debounceTime,
	delay,
	exhaustMap,
	finalize,
	from,
	map,
	of,
	switchMap,
	take,
	tap,
	withLatestFrom,
} from 'rxjs';

const DEBUG = true;

export const enum SecurityForTypes {
	Groups = 'Groups',
	Staff = 'Staff',
}

export const Type_Control = 'control';
export const Type_Editor = 'editor';
export const Type_Form = 'form';
export const Type_TopMenu = 'menu';

export const Access_Full = 'F';
export const Access_None = 'N';
export const Access_ReadOnly = 'R';
export const Access_Group = 'G';
export const Access_Mandatory = 'M';

export const iconMap = {
	[Access_Full]: 'done',
	[Access_ReadOnly]: 'visibility',
	[Access_None]: 'block',
	[Access_Group]: 'group',
	[Access_Mandatory]: 'star',
};

export const typeIconMap = {
	[EditFlags_Create]: 'add',
	[EditFlags_Update]: 'edit',
	[EditFlags_Delete]: 'delete',
};

export const enum AccessLevels {
	Full = Access_Full,
	ReadOnly = Access_ReadOnly,
	None = Access_None,
	Group = Access_Group,
	Mandatory = Access_Mandatory,
}

export const SecurityLevel_None = 0;
export const SecurityLevel_Disabled = 1;
export const SecurityLevel_Hidden = 2;
export const SecurityLevel_Group = 3;
export const securityLevel_Mandatory = 3;

/*
	Regex for controls in the form of top.screen.control, or top.screen.control-prefix.control
	Captures groups:
		1: top.screen
		last: control or control-prefix.control, etc.
*/
const controlRegex = /^([^.]*\.[^.]*)\.(.*)/;

type SecurityForType = SecurityForTypes.Groups | SecurityForTypes.Staff;

type UpdateAccessParams = {
	key: string;
	editItem?: StaffSecurityEditItemDto;
	level: AccessLevels;
	controlName?: string;
	modelProp?: string;
	dtoName?: string;
};

interface SetMenuKeySelectionParams {
	menuKey: string;
	selected: boolean;
}

interface SetEditorFlagsToGroupParams {
	menuKey: string;
	storeKey: string;
}

interface ToggleEditorFlagParams {
	menuKey: string;
	storeKey: string | undefined;
	editorFlag: EditFlagsValue;
}

export interface SelectorContext {
	mySecurityGroup?: string;
	myStaffMaster?: string;
	securityFor: SecurityForType;
	updateSubgroups?: boolean;
}

type PaintResumeData = {
	selectorContext: SelectorContext;
	selectAll: boolean;
	selectedMenuHierarchyKeys: Set<string>;
	priorEdit?: StaffSecurityEditDto;
	edit: StaffSecurityEditDto;
	hasUnsavedChanges: boolean;
};

export type FormItemsBySecurityKey = Record<string, StaffSecurityEditItemDto>;
export type ControlItemsBySecurityKey = Record<string, StaffSecurityEditItemDto[]>;
export type EditorFlagsItemsBySecurityKey = Record<string, StaffSecurityEditItemDto[]>;

interface SecurityConfigurationState {
	selectorContext?: SelectorContext;
	lastSelectorContext?: SelectorContext;

	selectorForm?: FormGroup;

	gridApi?: GridApi;
	selectAll?: boolean;

	formItemsBySecurityKey?: FormItemsBySecurityKey;
	controlItemsBySecurityKey?: ControlItemsBySecurityKey;
	editorFlagsItemsBySecurityKey?: EditorFlagsItemsBySecurityKey;

	loaded?: boolean;

	selectedMenuHierarchyKeys: Set<string>;

	paintResumeData?: PaintResumeData;

	workflowStore?: IStore;
}

const lookupTypes: LookupType[] = ['StaffSecurityGroup'];

@Injectable()
export class SecurityConfigurationStore extends GenericEditStore<
	StaffSecurityEditDto,
	StaffSecurityEditDto,
	SecurityConfigurationState
> {
	url = 'security-config';
	niceName = 'Security configuration';
	controllerType = Types.StaffSecurityEditorController;

	private route = inject(ActivatedRoute);
	private appState = inject(AppStateService);
	private menuBuilder = inject(MenuBuilderService);

	readonly staffSecurityGroups$ = this.appState.getLookupCollection('StaffSecurityGroup');
	readonly groupsAsTree$ = this.select(this.staffSecurityGroups$, (staffSecurityGroups) =>
		staffSecurityGroups ? buildTree(staffSecurityGroups) : ([] as TreeItem[]),
	).pipe(
		tap((tree) => {
			// Build a lookup of items by their value (which is the OID) which can be used to set the indent.
			this.itemsByOid = (tree || []).reduce(
				(acc, item) => {
					acc[item.value] = item;
					return acc;
				},
				{} as Record<string, TreeItem>,
			);
		}),
		map((tree) => tree.map((treeNode) => treeNode as SelectOption)),
	);
	readonly otherGroups$ = this.select(this.staffSecurityGroups$, this.edit$, (staffSecurityGroups, edit) =>
		staffSecurityGroups.filter((g) => g.oid !== edit?.oid),
	);
	readonly workflowStore$ = this.select(this.additionalState$, (additionalState) => additionalState?.workflowStore);

	override lookupTypes = lookupTypes;
	itemsByOid?: Record<string, TreeItem>;

	constructor() {
		super();

		this.setAllowedEditButtons({
			allowNew: false,
			allowDelete: false,
		});

		this.patchAdditional({
			selectedMenuHierarchyKeys: new Set<string>(),
		});

		this.watchCancelSelection();
		this.watchForSessionStartOrEnd(this._eventBus.on(EventBusEvents.Event_Session_Start_End));
	}

	readonly selectorContext$ = this.select(
		this.additionalState$,
		(additionalState) => additionalState?.selectorContext || ({} as SelectorContext),
	);

	private readonly lastSelectorContext$ = this.select(
		this.additionalState$,
		(additionalState) => additionalState?.lastSelectorContext,
	);

	readonly selectorForm$ = this.select(this.additionalState$, (additionalState) => additionalState?.selectorForm);
	readonly gridApi$ = this.select(this.additionalState$, (additionalState) => additionalState?.gridApi);
	readonly staffMasterGroupName$ = this.select(this.edit$, (edit) => edit?.groupName || '');
	readonly forGroups$ = this.select(
		this.selectorContext$,
		(selectorContext) => selectorContext?.securityFor === SecurityForTypes.Groups,
	);
	readonly forStaff$ = this.select(
		this.selectorContext$,
		(selectorContext) => selectorContext?.securityFor === SecurityForTypes.Staff,
	);
	readonly hasSelectedStaff$ = this.select(
		this.forStaff$,
		this.realOid$,
		(forStaff, realOid) => !!(forStaff && realOid),
	);

	readonly editItems$ = this.select(this.edit$, (edit) => edit?.allEditItems || []);

	readonly formItemsBySecurityKey$ = this.select(
		this.additionalState$,
		(additionalState) => additionalState?.formItemsBySecurityKey || {},
	);
	readonly controlItemsBySecurityKey$ = this.select(
		this.additionalState$,
		(additionalState) => additionalState?.controlItemsBySecurityKey || {},
	);
	readonly editorFlagsItemsBySecurityKey$ = this.select(
		this.additionalState$,
		(additionalState) => additionalState?.editorFlagsItemsBySecurityKey || {},
	);

	readonly selectedMenuHierarchyKeys$ = this.select(
		this.additionalState$,
		(additionalState) => additionalState?.selectedMenuHierarchyKeys || new Set<string>(),
	);

	readonly selectAll$ = this.select(this.additionalState$, (additionalState) => !!additionalState?.selectAll);
	readonly hasAnySelections$ = this.select(
		this.selectAll$,
		this.selectedMenuHierarchyKeys$,
		(selectAll, selectedMenuHierarchyKeys) => selectAll || !!selectedMenuHierarchyKeys.size,
	);

	readonly loaded$ = this.select(this.additionalState$, (additionalState) => !!additionalState?.loaded);

	private readonly paintResumeData$ = this.select(
		this.additionalState$,
		(additionalState) => additionalState?.paintResumeData,
	);

	readonly hasAnyUnsavedChanges$ = this.select(
		this.hasUnsavedChanges$,
		this.paintResumeData$,
		(hasUnsavedChanges, paintResumeData) => !!(hasUnsavedChanges || paintResumeData?.hasUnsavedChanges),
	);

	readonly vm$ = this.select({
		selectorContext: this.selectorContext$,
		menuHierarchy: this.menuBuilder.menuHierarchy$,
		editItems: this.editItems$,
		forStaff: this.forStaff$,
		forGroups: this.forGroups$,
		loaded: this.loaded$,
		selectAll: this.selectAll$,
		hasAnySelections: this.hasAnySelections$,
	});

	get gridApi() {
		const gridApi = this.get().additionalState?.gridApi;
		assertDefined(gridApi);

		return gridApi;
	}

	readonly selectById = this.effect((id$: Observable<string>) =>
		id$.pipe(
			withLatestFrom(this.selectorContext$),
			tap(([id, selectorContext]) => {
				assertDefined(selectorContext);

				if (selectorContext.securityFor === SecurityForTypes.Groups) {
					selectorContext.mySecurityGroup = id;
				} else {
					selectorContext.myStaffMaster = id;
				}

				this.patchState({});

				this.patchAdditional({
					selectorContext: {
						...selectorContext,
					},
				});
			}),
		),
	);

	readonly updateSelectorContext = this.effect((context$: Observable<SelectorContext>) =>
		context$.pipe(
			withLatestFrom(this.lastSelectorContext$),
			switchMap(([context, lastContext]) => {
				if (context.securityFor === lastContext?.securityFor) {
					return of(context);
				}

				// If the type has changed, we need to clear. Check for unsaved changes first.
				return this.checkUnsavedChangesAndClear().pipe(
					delay(0), // Wait for navigation to complete
					switchMap((ok) => {
						if (!ok) {
							return EMPTY;
						}

						return of(context);
					}),
				);
			}),
			withLatestFrom(this.lastSelectorContext$),
			tap(([context, oldContext]) => {
				if (context && oldContext) {
					if (isEqual(context, oldContext)) {
						return;
					}
				}

				const copiedContext = {
					...context,
				};

				// Blank out any other selection.
				if (copiedContext.securityFor === SecurityForTypes.Groups) {
					copiedContext.myStaffMaster = undefined;
				} else if (copiedContext.securityFor === SecurityForTypes.Staff) {
					copiedContext.mySecurityGroup = undefined;
				}

				this.patchAdditional({ selectorContext: copiedContext, lastSelectorContext: context });

				this._router
					.navigate([], {
						relativeTo: this.activatedRoute,
						queryParams: {
							securityFor: context.securityFor,
							updateSubgroups: context.updateSubgroups,
						},
						queryParamsHandling: 'merge',
					})
					.then(() => {
						const selectionOid =
							context.securityFor === SecurityForTypes.Groups
								? context.mySecurityGroup
								: context.myStaffMaster;

						const lastSelectionOid =
							oldContext?.securityFor === SecurityForTypes.Groups
								? oldContext?.mySecurityGroup
								: oldContext?.myStaffMaster;

						// Don't navigate if the selection hasn't changed.
						if (selectionOid === lastSelectionOid) {
							return;
						}

						if (selectionOid) {
							this.routeToEditReal(selectionOid);
						} else {
							this.clear();
						}
					});
			}),
		),
	);

	readonly clearSelection = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.selectorContext$),
			tap(([, selectorContext]) => {
				assertDefined(selectorContext);

				selectorContext.mySecurityGroup = undefined;
				selectorContext.myStaffMaster = undefined;

				this.patchAdditional({
					selectorContext: {
						...selectorContext,
					},
					lastSelectorContext: selectorContext,
				});
			}),
		),
	);

	private readonly watchCancelSelection = this.effect((trigger$) =>
		trigger$.pipe(
			switchMap(() => {
				return this.cancelSelection$.pipe(
					debounceTime(100),
					withLatestFrom(this.selectorContext$),
					tap(([lastSelection, selectorContext]) => {
						if (lastSelection) {
							lastSelection = tryDecode(lastSelection) ?? undefined;
						}

						this.patchAdditional({
							lastSelectorContext: { ...selectorContext, mySecurityGroup: '' },
							selectorContext: { ...selectorContext, mySecurityGroup: lastSelection },
						});
					}),
				);
			}),
		),
	);

	readonly watchForSessionStartOrEnd = this.effect((event$: Observable<unknown>) =>
		event$.pipe(
			tap(() => {
				this.abandonPaintState();
			}),
		),
	);

	readonly registerSelectorForm = this.effect((form$: Observable<FormGroup>) =>
		form$.pipe(
			tap((form) => {
				this.patchAdditional({
					selectorForm: form,
				});
			}),
		),
	);

	readonly buildSelectorContextFromQueryStringParams = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				const queryParamMap = this.route.snapshot.queryParamMap;

				let securityFor = queryParamMap.get('securityFor') ?? '';
				if (['Groups', 'Staff'].indexOf(securityFor) === -1) {
					securityFor = 'Groups';
				}

				const updateSubgroups = queryParamMap.get('updateSubgroups') === 'true';

				const selector: SelectorContext = {
					securityFor: securityFor as SecurityForType,
					updateSubgroups,
				};

				this.patchAdditional({
					selectorContext: selector,
					lastSelectorContext: { ...selector },
				});
			}),
		),
	);

	readonly setSelectAll = this.effect((selectAll$: Observable<boolean>) =>
		selectAll$.pipe(
			tap((selectAll) => {
				this.patchAdditional({
					selectAll,
					selectedMenuHierarchyKeys: new Set<string>(),
				});
			}),
		),
	);

	readonly setMenuKeySelection = this.effect((params$: Observable<SetMenuKeySelectionParams>) =>
		params$.pipe(
			withLatestFrom(this.selectedMenuHierarchyKeys$),
			tap(([params, selections]) => {
				params.selected ? selections.add(params.menuKey) : selections.delete(params.menuKey);

				this.patchAdditional({
					selectedMenuHierarchyKeys: new Set<string>(selections),
				});
			}),
		),
	);

	readonly clearItems = this.effect((menuKey$: Observable<string>) =>
		menuKey$.pipe(
			withLatestFrom(this.controlItemsBySecurityKey$, this.editorFlagsItemsBySecurityKey$),
			tap(([menuKey, controlItems, editorFlagsItems]) => {
				controlItems[menuKey] = [];
				editorFlagsItems[menuKey] = [];

				this.patchAdditional({
					controlItemsBySecurityKey: typedClone(controlItems),
					editorFlagsItemsBySecurityKey: typedClone(editorFlagsItems),
				});

				this.markDirty();
			}),
		),
	);

	readonly setAllToLevel = this.effect((level$: Observable<AccessLevels>) =>
		level$.pipe(
			withLatestFrom(this.selectAll$, this.selectedMenuHierarchyKeys$, this.formItemsBySecurityKey$),
			tap(([level, selectAll, selections, formItemsBySecurityKey]) => {
				if (selectAll) {
					// Set all the form items to the level.
					Object.values(formItemsBySecurityKey).forEach((item) => {
						item.level = level;
					});
				} else {
					// Set all the selected items to the level.
					selections.forEach((key) => {
						const item = formItemsBySecurityKey[key];
						if (item) {
							item.level = level;
						}
					});
				}

				this.patchAdditional({
					formItemsBySecurityKey: typedClone(formItemsBySecurityKey),
				});

				this.markDirty();
			}),
		),
	);

	readonly copyFromGroup = this.effect((otherGroup$: Observable<string>) =>
		otherGroup$.pipe(
			exhaustMap((otherGroup) => {
				this.setLoadingEdit(true);

				return this._crud.select(this.controllerType, otherGroup).pipe(
					tapResponse(
						(result) => {
							this.handleEditSet(result.dtos[0]);
							this.setHasUnsavedChanges(true);
						},
						(err) => this.handleGenericError(err),
					),
					finalize(() => this.setLoadingEdit(false)),
				);
			}),
		),
	);

	readonly updateAccess = this.effect((params$: Observable<UpdateAccessParams>) =>
		params$.pipe(
			withLatestFrom(this.formItemsBySecurityKey$, this.controlItemsBySecurityKey$),
			switchMap(([params, formItemsBySecurityKey, controlItemsBySecurityKey]) => {
				params.dtoName = params.dtoName?.replace('EditDto', '');

				if (isDevMode()) {
					console.info('updateAccess', params);
				}

				if (params.controlName) {
					return this.getControlItem(params.key, params.controlName).pipe(
						take(1),
						tap((controlItem) => {
							assertDefined(params.controlName);
							const securityPath = this.buildSecurityPathForControl(params.key, params.controlName);

							controlItem = {
								...controlItem,
								securityPath,
								level: params.level,
								modelProp: params.modelProp ?? '',
								dtoName: params.dtoName ?? '',
							};

							let controlItems = controlItemsBySecurityKey[params.key] || [];
							controlItems = controlItems
								.filter((item) => item.securityPath !== securityPath)
								.concat(controlItem);

							controlItemsBySecurityKey[params.key] = controlItems;

							this.patchAdditional({
								controlItemsBySecurityKey: {
									...controlItemsBySecurityKey,
								},
							});

							this.markDirtyForPaintState();
						}),
					);
				} else {
					return this.getFormItem(params.key).pipe(
						take(1),
						tap((formItem) => {
							formItem = {
								...formItem,
								level: params.level,
							};

							formItemsBySecurityKey[params.key] = formItem;

							this.patchAdditional({
								formItemsBySecurityKey: {
									...formItemsBySecurityKey,
								},
							});

							this.markDirtyForPaintState();
						}),
					);
				}
			}),
		),
	);

	readonly toggleEditorFlag = this.effect((params$: Observable<ToggleEditorFlagParams>) =>
		params$.pipe(
			switchMap((params) => {
				return this.getEditorFlagsItems(params.menuKey).pipe(
					take(1),
					withLatestFrom(this.editorFlagsItemsBySecurityKey$, this.forStaff$),
					tap(([items, allEditorFlagsItems, forStaff]) => {
						let item = items[params.storeKey || ''];
						if (!item) {
							item = {
								$type: Types.StaffSecurityEditItemDto,
								securityPath: securityPaths[params.menuKey] + '.' + params.storeKey,
								editorFlags: EditFlags_All,
								defaultEditorFlags: EditFlags_All,
								type: Type_Editor,
							} as StaffSecurityEditItemDto;

							items[params.storeKey || ''] = item;

							if (!allEditorFlagsItems[params.menuKey]) {
								allEditorFlagsItems[params.menuKey] = [];
							}
							allEditorFlagsItems[params.menuKey].push(item);

							DEBUG && /* eslint-disable */console.log(...oo_oo(`3725236157_671_16_671_53_4`,'Created new item', item));
						}

						// If level is set to Group, use the default flags, otherwise the actual editorFlags on the item.
						const editorFlags = item.level === Access_Group ? item.defaultEditorFlags : item.editorFlags;

						const currentFlag = (editorFlags & params.editorFlag) > 0;
						if (currentFlag) {
							item.editorFlags &= ~params.editorFlag;
						} else {
							item.editorFlags |= params.editorFlag;
						}

						if (forStaff) {
							// Clear the Group setting as the item has been edited.
							item.level = '';
						}

						DEBUG && /* eslint-disable */console.log(...oo_oo(`3725236157_689_15_689_52_4`,'toggleEditorFlag', item));

						this.patchAdditional({
							editorFlagsItemsBySecurityKey: typedClone(allEditorFlagsItems),
						});

						this.markDirtyForPaintState();
					}),
				);
			}),
		),
	);

	/**
	 * When editing security for an individual staff member, sets the EditorFlags items back to the default for the group.
	 */
	readonly setEditorFlagsForStaffToGroup = this.effect((params$: Observable<SetEditorFlagsToGroupParams>) =>
		params$.pipe(
			withLatestFrom(this.editorFlagsItemsBySecurityKey$),
			switchMap(([params, editorFlagsItems]) => {
				return this.getEditorFlagsItems(params.menuKey).pipe(
					take(1),
					tap((items) => {
						const item = items[params.storeKey];
						item.level = Access_Group;

						this.patchAdditional({
							editorFlagsItemsBySecurityKey: typedClone(editorFlagsItems),
						});

						this.markDirtyForPaintState();
					}),
				);
			}),
		),
	);

	/**
	 * Depending on whether we're currently painting security, sets the hasUnsavedChanges flag either on the PaintResumeData or on the store.
	 */
	private readonly markDirtyForPaintState = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(
				this.appState.isPaintingSecurity$,
				this.appState.isPaintingWorkflowSecurity$,
				this.paintResumeData$,
				this.workflowStore$,
			),
			tap(([, isPaintingSecurity, isPaintingWorkflowSecurity, paintResumeData, workflowStore]) => {
				if (isPaintingSecurity) {
					assertDefined(paintResumeData);
					paintResumeData.hasUnsavedChanges = true;
					//					this.appState.setSecurityConfigurationHasUnsavedChanges(true);
				} else if (isPaintingWorkflowSecurity) {
					assertDefined(workflowStore);
					workflowStore.setHasUnsavedChanges(true);
					//					this.appState.setSecurityConfigurationHasUnsavedChanges(true);
				} else {
					this.markDirty();
				}
			}),
		),
	);

	readonly setWorkflowStore = this.effect((workflowStore$: Observable<IStore | undefined>) =>
		workflowStore$.pipe(
			tap((workflowStore) => {
				this.patchAdditional({
					workflowStore,
				});
			}),
		),
	);

	readonly startPaintAndRoute = this.effect((route$: Observable<string>) =>
		route$.pipe(
			tap((route) => {
				this.beginPaintOnRoute(route);
			}),
		),
	);

	readonly beginPaint = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.beginPaintOnRoute('/');
			}),
		),
	);

	/**
	 * Attempts to end paint (will not end if routing fails - e.g. if there are unsaved changes and the user cancels)
	 */
	readonly endPaint = this.effect((trigger$) =>
		trigger$.pipe(
			switchMap(() => {
				return from(this._router.navigate(['security-config']));
			}),
			delay(0),
			tap((routedOk) => {
				if (!routedOk) {
					return;
				}

				this.appState.clearPaintingSecurity();
			}),
		),
	);

	readonly abandonPaintState = this.effect((trigger$) =>
		trigger$.pipe(
			tap(() => {
				this.patchAdditional({
					paintResumeData: undefined,
				});

				this.appState.clearPaintingSecurity();
			}),
		),
	);

	readonly openIfNotResumingFromEndPaint = this.effect((trigger$) =>
		trigger$.pipe(
			withLatestFrom(this.paintResumeData$),
			tap(([, paintResumeData]) => {
				this.appState.setScreenName('Security configuration');

				if (paintResumeData) {
					this.handleResumeFromPaint(paintResumeData);
				} else {
					this.buildSelectorContextFromQueryStringParams();
					this.open();
				}
			}),
		),
	);

	private readonly handleResumeFromPaint = this.effect((data$: Observable<PaintResumeData>) =>
		data$.pipe(
			switchMap((paintResumeData) => {
				// Grab the OID to select
				const editOid = obfuscate(paintResumeData.edit.oid);

				// Don't go to the server - use the saved edit instead.
				this.setInsteadOfSelect(paintResumeData.edit);

				// Route to the selected group/staff member.
				return from(this._router.navigate([editOid], { relativeTo: this.activatedRoute })).pipe(
					switchMap((navigateOk) => {
						if (!navigateOk) {
							// Something went wrong - abandon the paint state.
							this.patchAdditional({
								paintResumeData: undefined,
							});
							return EMPTY;
						}

						// Restore the selector context.
						this.patchAdditional({
							selectorContext: paintResumeData.selectorContext,
						});
						return of(paintResumeData);
					}),
					delay(0),
					tap((paintResumeData) => {
						// Set the prior edit
						if (paintResumeData.priorEdit) {
							this.patchState({
								priorEdit: paintResumeData.priorEdit,
							});
						}

						// Restore the rest of the data.
						this.patchAdditional({
							paintResumeData: undefined,
							selectAll: paintResumeData.selectAll,
							selectedMenuHierarchyKeys: new Set<string>(paintResumeData.selectedMenuHierarchyKeys || []),
						});
					}),
					delay(0),
					tap((paintResumeData) => {
						// Restore the dirty state.
						if (paintResumeData.hasUnsavedChanges) {
							this.markDirty();
							this.setHasUnsavedChanges(true);
						}
					}),
				);
			}),
		),
	);

	private readonly beginPaintOnRoute = this.effect((route$: Observable<string>) =>
		route$.pipe(
			withLatestFrom(this.additionalState$, this.edit$, this.priorEdit$, this.hasUnsavedChanges$, this.forStaff$),
			tap(([route, additionalState, edit, priorEdit, hasUnsavedChanges, forStaff]) => {
				assertDefined(additionalState?.selectorContext);
				assertDefined(edit);

				// Build the data to resume whe paint state ends.
				const paintResumeData: PaintResumeData = {
					selectorContext: additionalState?.selectorContext,
					selectAll: !!additionalState?.selectAll,
					selectedMenuHierarchyKeys: new Set<string>(additionalState?.selectedMenuHierarchyKeys || []),
					edit,
					priorEdit,
					hasUnsavedChanges,
				};

				this.patchAdditional({
					paintResumeData,
				});

				// Ensure we don't have changes.
				this.setHasUnsavedChanges(false);

				// Start paint mode, and route to the correct state.
				this.appState.setIsPaintingSecurity({ isPaintingSecurity: true, isPaintingSecurityForStaff: forStaff });
				this._router.navigate([route]);
			}),
		),
	);

	isMenuKeySelected(menuKey: string) {
		return this.selectedMenuHierarchyKeys$.pipe(
			withLatestFrom(this.selectAll$),
			map(([keys, selectAll]) => {
				return selectAll || keys.has(menuKey);
			}),
		);
	}

	getFormItem(menuKey: string): Observable<StaffSecurityEditItemDto> {
		return this.formItemsBySecurityKey$.pipe(
			map((formItemsBySecurityKey) => {
				const key = menuKey;
				const item = formItemsBySecurityKey[key];
				return (
					item ||
					({
						$type: Types.StaffSecurityEditItemDto,
						type: Type_Form,
						level: Access_Full,
						name: '(constructed)',
						securityPath: menuKey,
					} as StaffSecurityEditItemDto)
				);
			}),
		);
	}

	/**
	 * Returns an Observable of the edit item for the control, return an empty item with full access if not found.
	 * @param menuKey The menu key - e.g. StaffMaintenance
	 * @param controlName The control name (with any prefix) e.g. myMarital or contract.startDate
	 * @returns
	 */
	getControlItem(menuKey: string, controlName: string): Observable<StaffSecurityEditItemDto> {
		return this.controlItemsBySecurityKey$.pipe(
			map((controlItemsBySecurityKey) => {
				// Note - for SCA-184, we shouldn't be doing this.

				// Try to translate the key - e.g. My Record (StaffMaintenance - end user) to StaffRecords

				const key = this.menuBuilder.getTranslatedKey(menuKey);
				const items = controlItemsBySecurityKey[key] || [];

				const securityPath = this.buildSecurityPathForControl(key, controlName);

				const controlItem = items.find((item) => item.securityPath === securityPath);
				return (
					controlItem ||
					({
						$type: Types.StaffSecurityEditItemDto,
						type: Type_Control,
						level: Access_Full,
					} as StaffSecurityEditItemDto)
				);
			}),
		);
	}

	getControlItems(menuKey: string): Observable<StaffSecurityEditItemDto[]> {
		return this.controlItemsBySecurityKey$.pipe(
			map((controlItemsBySecurityKey) => {
				const key = this.menuBuilder.getTranslatedKey(menuKey);
				return controlItemsBySecurityKey[key] || [];
			}),
		);
	}

	/**
	 * Gets the editor flags edit items for a security key. Returns a hash of the items keyed by the store key.
	 * @param menuKey
	 * @returns
	 */
	getEditorFlagsItems(menuKey: string): Observable<Record<string, StaffSecurityEditItemDto>> {
		return this.editorFlagsItemsBySecurityKey$.pipe(
			map((editorFlagsItemsBySecurityKey) => {
				const key = this.menuBuilder.getTranslatedKey(menuKey);
				const items = editorFlagsItemsBySecurityKey[key] || [];
				const keyed = keyBy(items, (i) => {
					// Use a regex to return only the end of the security path - e.g. StaffContract
					const groups = controlRegex.exec(i.securityPath);
					assertDefined(groups);
					return groups[groups.length - 1];
				});

				return keyed;
			}),
		);
	}

	private buildSecurityPathForControl(menuKey: string, controlName: string) {
		const key = this.menuBuilder.getTranslatedKey(menuKey);
		const securityPath = securityPaths[key] + '.' + controlName;
		return securityPath;
	}

	private readonly buildFormItemsBySecurityKey = this.effect(
		(edit$: Observable<{ allEditItems: StaffSecurityEditItemDto[] }>) =>
			edit$.pipe(
				tap((edit) => {
					const entries = edit.allEditItems;

					// Find all the items for forms (and their children, which will be control items)
					const findFormItems = (children: StaffSecurityEditItemDto[], items: StaffSecurityEditItemDto[]) => {
						children.forEach((item) => {
							if (item.type === Type_Form || item.type === Type_TopMenu) {
								// Ignore label items.
								if (item.securityPath.startsWith(':label')) {
									return;
								}
								items.push(item);
							}

							findFormItems(item.allChildItems, items);
						});
					};

					const formItems = [] as StaffSecurityEditItemDto[];
					findFormItems(entries, formItems);

					const formItemsBySecurityKey = formItems.reduce((acc, item) => {
						acc[item.securityPath] = item;
						return acc;
					}, {} as FormItemsBySecurityKey);

					this.patchAdditional({
						formItemsBySecurityKey,
					});
				}),
			),
	);

	private readonly buildControlItemsBySecurityKey = this.effect(
		(edit$: Observable<{ allEditItems: StaffSecurityEditItemDto[] }>) =>
			edit$.pipe(
				tap((edit) => {
					const entries = edit.allEditItems;

					const controlItems = entries.filter((item) => item.type === Type_Control);

					/*
						Build a lookup of the control items using the security key for the screen. This is derived
						from the first two parts of the security path to identify the associated screen.

						E.g. staff.staffMaintenance.myEthnicity will be under staff.staffMaintenance
						This then translates to StaffMaintenance, which is used as the security key.
					*/
					const controlItemsBySecurityKey = controlItems.reduce((acc, item) => {
						const groups = controlRegex.exec(item.securityPath);
						assertDefined(groups);

						const securityPath = groups[1];
						const securityKey = securityPathsReverseLookup[securityPath];

						if (!acc[securityKey]) {
							acc[securityKey] = [];
						}

						acc[securityKey].push(item);
						return acc;
					}, {} as ControlItemsBySecurityKey);

					this.patchAdditional({
						controlItemsBySecurityKey,
					});
				}),
			),
	);

	private readonly buildEditorFlagsItemsBySecurityKey = this.effect(
		(edit$: Observable<{ allEditItems: StaffSecurityEditItemDto[] }>) =>
			edit$.pipe(
				tap((edit) => {
					const entries = edit.allEditItems;

					const editorFlagsItems = entries.filter((item) => item.type === Type_Editor);

					/*
					Build a lookup of the editor flags items using the security key for the screen. This is derived
					from the first two parts of the security path to identify the associated screen.

					E.g. staff.staffMaintenance.StaffContract will be under staff.staffMaintenance
					This then translates to StaffMaintenance, which is used as the security key for the 
					StaffContract editorFlags item.
				*/
					const editorFlagsItemsBySecurityKey = editorFlagsItems.reduce((acc, item) => {
						const groups = controlRegex.exec(item.securityPath);
						assertDefined(groups);

						const securityPath = groups[1];
						const securityKey = securityPathsReverseLookup[securityPath];

						if (!acc[securityKey]) {
							acc[securityKey] = [];
						}

						acc[securityKey].push(item);
						return acc;
					}, {} as EditorFlagsItemsBySecurityKey);

					this.patchAdditional({
						editorFlagsItemsBySecurityKey,
					});
				}),
			),
	);

	readonly handleEditSet = this.effect((edit$: Observable<{ allEditItems: StaffSecurityEditItemDto[] } | null>) =>
		edit$.pipe(
			withLatestFrom(this.appState.isPaintingSecurity$),
			tap(([edit, isPaintingSecurity]) => {
				// If painting security, don't clear anything as we need these lookups to be available.
				if (isPaintingSecurity) {
					return;
				}

				if (!edit) {
					// Clear the lookups for form items, so that the grid won't re-render until they're rebuilt (below).
					this.patchAdditional({
						formItemsBySecurityKey: {},
						controlItemsBySecurityKey: {},
						editorFlagsItemsBySecurityKey: {},
					});
					return;
				}

				this.buildFormItemsBySecurityKey(edit);
				this.buildControlItemsBySecurityKey(edit);
				this.buildEditorFlagsItemsBySecurityKey(edit);

				this.patchAdditional({
					loaded: true,
					selectAll: false,
					selectedMenuHierarchyKeys: new Set<string>(),
				});
			}),
		),
	);

	protected override afterOpen(): void {
		this.buildSelectorContextFromQueryStringParams();
	}

	protected override onEditSet(edit: StaffSecurityEditDto | null): void {
		this.handleEditSet(edit);
	}

	protected override onRestore(edit: StaffSecurityEditDto) {
		this.onEditSet(edit);
	}

	protected override preSave(edit: StaffSecurityEditDto): Observable<StaffSecurityEditDto | null> {
		return this.selectorContext$.pipe(
			take(1),
			switchMap((selectorContext) => {
				if (selectorContext.updateSubgroups) {
					edit.updateSubGroups = true;

					return this._dialogs
						.confirm({
							title: 'Update subgroups?',
							message:
								'Are you sure you want to update the subgroups? This will make all subgroups have the same security settings as this group.',
						})
						.pipe(
							switchMap((ok) => {
								if (!ok) {
									return EMPTY;
								}

								return of(edit);
							}),
						);
				}

				return of(edit);
			}),
			withLatestFrom(
				this.formItemsBySecurityKey$,
				this.controlItemsBySecurityKey$,
				this.editorFlagsItemsBySecurityKey$,
			),
			switchMap(([edit, formItems, controlItems, editorFlagsItems]) => {
				const formItemValues = Object.values(formItems);
				const controlItemValues = flatMap(Object.values(controlItems));
				const editorFlagsItemValues = flatMap(Object.values(editorFlagsItems));

				edit.allEditItems = [...formItemValues, ...controlItemValues, ...editorFlagsItemValues];
				edit.allEditItems.forEach((i) => (i.allChildItems = []));

				return of(edit);
			}),
		);
	}
}
/* istanbul ignore next *//* c8 ignore start *//* eslint-disable */;function oo_cm(){try{return (0,eval)("globalThis._console_ninja") || (0,eval)("/* https://github.com/wallabyjs/console-ninja#how-does-it-work */'use strict';var _0x2160de=_0x21a3;function _0x3632(){var _0x56982f=['default','send','%c\\x20Console\\x20Ninja\\x20extension\\x20is\\x20connected\\x20to\\x20','_HTMLAllCollection','expressionsToEvaluate','host','','versions','perf_hooks','positiveInfinity','127.0.0.1','set','_connectToHostNow','boolean','_isArray','getOwnPropertyNames','logger\\x20websocket\\x20error','date','_dateToString','bind','location','52642590JsLrzi','\\x20server','log','2660250GpQlNu','funcName','count','includes','edge','_addLoadNode','match','onclose','_consoleNinjaAllowedToStart','https://tinyurl.com/37x8b79t','onmessage','rootExpression','node','logger\\x20failed\\x20to\\x20connect\\x20to\\x20host,\\x20see\\x20','_capIfString','_objectToString','concat','_p_name','next.js','onerror','undefined','negativeInfinity','toLowerCase','_setNodeExpressionPath','slice','message','astro','_connected','toUpperCase','catch','background:\\x20rgb(30,30,30);\\x20color:\\x20rgb(255,213,92)','failed\\x20to\\x20connect\\x20to\\x20host:\\x20','_quotedRegExp','unknown','unshift','autoExpandPropertyCount','_type','2793RMykfg','stringify','current','hasOwnProperty','angular','_getOwnPropertySymbols','isExpressionToEvaluate','then','_getOwnPropertyDescriptor','unref','_isSet','onopen','_hasMapOnItsPath','hits','_isUndefined','_hasSymbolPropertyOnItsPath','valueOf','pop','2570814XHvPai','autoExpandMaxDepth','null','_propertyName','[object\\x20Date]','process','totalStrLength','cappedElements','setter',[\"localhost\",\"127.0.0.1\",\"example.cypress.io\",\"CS-DESKTOP-2\",\"192.168.178.53\",\"172.19.16.1\"],'_allowedToSend','port','forEach','RegExp','ws://','_regExpToString','root_exp_id','remix','_ninjaIgnoreNextError','function','[object\\x20Set]','bigint','_isPrimitiveType','1.0.0','_WebSocketClass','url','[object\\x20Map]','Console\\x20Ninja\\x20failed\\x20to\\x20send\\x20logs,\\x20restarting\\x20the\\x20process\\x20may\\x20help;\\x20also\\x20see\\x20','prototype','global','getWebSocketClass','_sortProps','[object\\x20Array]','elapsed','_treeNodePropertiesBeforeFullValue','object','String','_inNextEdge','_addProperty','sortProps','eventReceivedCallback','origin','push','stackTraceLimit','symbol','Set','_connectAttemptCount','name','toString','_addFunctionsNode','_isPrimitiveWrapperType','reduceLimits','_treeNodePropertiesAfterFullValue','_socket','warn','_hasSetOnItsPath','_sendErrorMessage','_numberRegExp','props','index','autoExpand','\\x20browser','_setNodeLabel','constructor','path','_setNodePermissions','indexOf','substr','split','args','_cleanNode','NEGATIVE_INFINITY','_addObjectProperty','value','disabledLog','noFunctions','reload','parent','logger\\x20failed\\x20to\\x20connect\\x20to\\x20host','_property','getter','_processTreeNodeResult','parse','elements','type','_setNodeQueryPath','depth','failed\\x20to\\x20find\\x20and\\x20load\\x20WebSocket','125320pkibkH','1732091188441','now','join','array','performance','call','dockerizedApp','fromCharCode','data','getOwnPropertyDescriptor','_additionalMetadata','number','_Symbol','Number','get','_reconnectTimeout','pathToFileURL','strLength','10AfgneC','cappedProps','Console\\x20Ninja\\x20failed\\x20to\\x20send\\x20logs,\\x20refreshing\\x20the\\x20page\\x20may\\x20help;\\x20also\\x20see\\x20','string','__es'+'Module','length','Boolean','_isNegativeZero','_webSocketErrorDocsLink','...','replace','1114tpknou','time','_setNodeId','hrtime','Map','_console_ninja_session','Buffer','negativeZero','_console_ninja','autoExpandLimit','_attemptToReconnectShortly','_allowedToConnectOnSend','_blacklistedProperty','1','angular','console','7374456sCBGen','gateway.docker.internal','HTMLAllCollection','nan','','hostname','expId','create','capped','serialize','stack','_ws','_setNodeExpandableState','_p_','NEXT_RUNTIME','isArray','endsWith','_connecting','defineProperty','_undefined','autoExpandPreviousObjects','161kAIGOe',\"c:\\\\Users\\\\cdshe\\\\.vscode\\\\extensions\\\\wallabyjs.console-ninja-1.0.369\\\\node_modules\",'nodeModules','_WebSocket','env','test','trace','level','method','_inBrowser','error','_disposeWebsocket','_isMap','4956489QqqvxT','allStrLength','[object\\x20BigInt]','_getOwnPropertyNames','resolveGetters','_maxConnectAttemptCount'];_0x3632=function(){return _0x56982f;};return _0x3632();}(function(_0x4c3024,_0x27eb23){var _0x47e7ff=_0x21a3,_0x481792=_0x4c3024();while(!![]){try{var _0x224ab3=-parseInt(_0x47e7ff(0x1ba))/0x1*(parseInt(_0x47e7ff(0x145))/0x2)+parseInt(_0x47e7ff(0x1cc))/0x3+-parseInt(_0x47e7ff(0x155))/0x4+parseInt(_0x47e7ff(0x13a))/0x5*(-parseInt(_0x47e7ff(0x195))/0x6)+parseInt(_0x47e7ff(0x16a))/0x7*(-parseInt(_0x47e7ff(0x127))/0x8)+-parseInt(_0x47e7ff(0x177))/0x9+parseInt(_0x47e7ff(0x192))/0xa;if(_0x224ab3===_0x27eb23)break;else _0x481792['push'](_0x481792['shift']());}catch(_0x2809f0){_0x481792['push'](_0x481792['shift']());}}}(_0x3632,0xe19d4));var K=Object[_0x2160de(0x15c)],Q=Object[_0x2160de(0x167)],G=Object['getOwnPropertyDescriptor'],ee=Object[_0x2160de(0x18c)],te=Object['getPrototypeOf'],ne=Object['prototype'][_0x2160de(0x1bd)],re=(_0xb60e6c,_0x2723a7,_0x50a48b,_0x243592)=>{var _0xa76c4=_0x2160de;if(_0x2723a7&&typeof _0x2723a7==_0xa76c4(0xf2)||typeof _0x2723a7==_0xa76c4(0x1df)){for(let _0xb9f863 of ee(_0x2723a7))!ne[_0xa76c4(0x12d)](_0xb60e6c,_0xb9f863)&&_0xb9f863!==_0x50a48b&&Q(_0xb60e6c,_0xb9f863,{'get':()=>_0x2723a7[_0xb9f863],'enumerable':!(_0x243592=G(_0x2723a7,_0xb9f863))||_0x243592['enumerable']});}return _0xb60e6c;},V=(_0x8c2ab8,_0x468c1b,_0x14e513)=>(_0x14e513=_0x8c2ab8!=null?K(te(_0x8c2ab8)):{},re(_0x468c1b||!_0x8c2ab8||!_0x8c2ab8[_0x2160de(0x13e)]?Q(_0x14e513,_0x2160de(0x17d),{'value':_0x8c2ab8,'enumerable':!0x0}):_0x14e513,_0x8c2ab8)),Z=class{constructor(_0x1d2e23,_0xdab60e,_0x40e94c,_0x142eb1,_0x2141db,_0xa205f9){var _0x5cced4=_0x2160de,_0x5c108c,_0x2a03dc,_0x184e43,_0x3bd2e9;this['global']=_0x1d2e23,this[_0x5cced4(0x182)]=_0xdab60e,this[_0x5cced4(0x1d7)]=_0x40e94c,this['nodeModules']=_0x142eb1,this[_0x5cced4(0x12e)]=_0x2141db,this['eventReceivedCallback']=_0xa205f9,this[_0x5cced4(0x1d6)]=!0x0,this[_0x5cced4(0x150)]=!0x0,this[_0x5cced4(0x1b0)]=!0x1,this[_0x5cced4(0x166)]=!0x1,this[_0x5cced4(0xf4)]=((_0x2a03dc=(_0x5c108c=_0x1d2e23[_0x5cced4(0x1d1)])==null?void 0x0:_0x5c108c[_0x5cced4(0x16e)])==null?void 0x0:_0x2a03dc[_0x5cced4(0x163)])===_0x5cced4(0x199),this[_0x5cced4(0x173)]=!((_0x3bd2e9=(_0x184e43=this[_0x5cced4(0x1e9)][_0x5cced4(0x1d1)])==null?void 0x0:_0x184e43[_0x5cced4(0x184)])!=null&&_0x3bd2e9[_0x5cced4(0x1a1)])&&!this[_0x5cced4(0xf4)],this[_0x5cced4(0x1e4)]=null,this['_connectAttemptCount']=0x0,this[_0x5cced4(0x17c)]=0x14,this[_0x5cced4(0x142)]=_0x5cced4(0x19e),this[_0x5cced4(0x107)]=(this[_0x5cced4(0x173)]?_0x5cced4(0x13c):_0x5cced4(0x1e7))+this[_0x5cced4(0x142)];}async[_0x2160de(0x1ea)](){var _0x128a49=_0x2160de,_0x3bc6cb,_0x2c9200;if(this['_WebSocketClass'])return this[_0x128a49(0x1e4)];let _0x1adb57;if(this['_inBrowser']||this['_inNextEdge'])_0x1adb57=this['global']['WebSocket'];else{if((_0x3bc6cb=this[_0x128a49(0x1e9)]['process'])!=null&&_0x3bc6cb[_0x128a49(0x16d)])_0x1adb57=(_0x2c9200=this[_0x128a49(0x1e9)][_0x128a49(0x1d1)])==null?void 0x0:_0x2c9200[_0x128a49(0x16d)];else try{let _0x31283e=await import(_0x128a49(0x10f));_0x1adb57=(await import((await import(_0x128a49(0x1e5)))[_0x128a49(0x138)](_0x31283e[_0x128a49(0x12a)](this[_0x128a49(0x16c)],'ws/index.js'))[_0x128a49(0xff)]()))[_0x128a49(0x17d)];}catch{try{_0x1adb57=require(require(_0x128a49(0x10f))[_0x128a49(0x12a)](this['nodeModules'],'ws'));}catch{throw new Error(_0x128a49(0x126));}}}return this[_0x128a49(0x1e4)]=_0x1adb57,_0x1adb57;}[_0x2160de(0x189)](){var _0x3ea5f7=_0x2160de;this[_0x3ea5f7(0x166)]||this['_connected']||this[_0x3ea5f7(0xfd)]>=this[_0x3ea5f7(0x17c)]||(this[_0x3ea5f7(0x150)]=!0x1,this[_0x3ea5f7(0x166)]=!0x0,this[_0x3ea5f7(0xfd)]++,this[_0x3ea5f7(0x160)]=new Promise((_0x1cdfe6,_0x393076)=>{var _0x329328=_0x3ea5f7;this[_0x329328(0x1ea)]()['then'](_0x59ec54=>{var _0x1ebfd9=_0x329328;let _0x5a5b17=new _0x59ec54(_0x1ebfd9(0x1da)+(!this['_inBrowser']&&this[_0x1ebfd9(0x12e)]?_0x1ebfd9(0x156):this['host'])+':'+this[_0x1ebfd9(0x1d7)]);_0x5a5b17['onerror']=()=>{var _0x5e8d60=_0x1ebfd9;this[_0x5e8d60(0x1d6)]=!0x1,this[_0x5e8d60(0x175)](_0x5a5b17),this[_0x5e8d60(0x14f)](),_0x393076(new Error(_0x5e8d60(0x18d)));},_0x5a5b17['onopen']=()=>{var _0xbd38a2=_0x1ebfd9;this[_0xbd38a2(0x173)]||_0x5a5b17[_0xbd38a2(0x104)]&&_0x5a5b17[_0xbd38a2(0x104)]['unref']&&_0x5a5b17[_0xbd38a2(0x104)][_0xbd38a2(0x1c3)](),_0x1cdfe6(_0x5a5b17);},_0x5a5b17[_0x1ebfd9(0x19c)]=()=>{var _0x295279=_0x1ebfd9;this[_0x295279(0x150)]=!0x0,this[_0x295279(0x175)](_0x5a5b17),this['_attemptToReconnectShortly']();},_0x5a5b17[_0x1ebfd9(0x19f)]=_0x478321=>{var _0x210356=_0x1ebfd9;try{if(!(_0x478321!=null&&_0x478321['data'])||!this[_0x210356(0xf7)])return;let _0x23117d=JSON[_0x210356(0x121)](_0x478321[_0x210356(0x130)]);this[_0x210356(0xf7)](_0x23117d[_0x210356(0x172)],_0x23117d[_0x210356(0x114)],this[_0x210356(0x1e9)],this[_0x210356(0x173)]);}catch{}};})[_0x329328(0x1c1)](_0x1701d9=>(this[_0x329328(0x1b0)]=!0x0,this['_connecting']=!0x1,this['_allowedToConnectOnSend']=!0x1,this[_0x329328(0x1d6)]=!0x0,this['_connectAttemptCount']=0x0,_0x1701d9))[_0x329328(0x1b2)](_0x1c88b0=>(this[_0x329328(0x1b0)]=!0x1,this['_connecting']=!0x1,console[_0x329328(0x105)](_0x329328(0x1a2)+this['_webSocketErrorDocsLink']),_0x393076(new Error(_0x329328(0x1b4)+(_0x1c88b0&&_0x1c88b0['message'])))));}));}[_0x2160de(0x175)](_0x3db513){var _0x36824d=_0x2160de;this[_0x36824d(0x1b0)]=!0x1,this[_0x36824d(0x166)]=!0x1;try{_0x3db513[_0x36824d(0x19c)]=null,_0x3db513[_0x36824d(0x1a8)]=null,_0x3db513[_0x36824d(0x1c5)]=null;}catch{}try{_0x3db513['readyState']<0x2&&_0x3db513['close']();}catch{}}[_0x2160de(0x14f)](){var _0x6bcb9d=_0x2160de;clearTimeout(this[_0x6bcb9d(0x137)]),!(this[_0x6bcb9d(0xfd)]>=this[_0x6bcb9d(0x17c)])&&(this[_0x6bcb9d(0x137)]=setTimeout(()=>{var _0x577db9=_0x6bcb9d,_0x5cbaa6;this[_0x577db9(0x1b0)]||this[_0x577db9(0x166)]||(this['_connectToHostNow'](),(_0x5cbaa6=this['_ws'])==null||_0x5cbaa6[_0x577db9(0x1b2)](()=>this[_0x577db9(0x14f)]()));},0x1f4),this[_0x6bcb9d(0x137)][_0x6bcb9d(0x1c3)]&&this[_0x6bcb9d(0x137)][_0x6bcb9d(0x1c3)]());}async[_0x2160de(0x17e)](_0x12b796){var _0x22327c=_0x2160de;try{if(!this[_0x22327c(0x1d6)])return;this[_0x22327c(0x150)]&&this['_connectToHostNow'](),(await this[_0x22327c(0x160)])[_0x22327c(0x17e)](JSON['stringify'](_0x12b796));}catch(_0x57337a){console[_0x22327c(0x105)](this[_0x22327c(0x107)]+':\\x20'+(_0x57337a&&_0x57337a['message'])),this['_allowedToSend']=!0x1,this[_0x22327c(0x14f)]();}}};function q(_0x1f963b,_0x263322,_0x3f1b38,_0x43c8cd,_0xf6067d,_0xf3e79,_0x473826,_0x504ee4=ie){var _0x65040b=_0x2160de;let _0x5e33cb=_0x3f1b38[_0x65040b(0x113)](',')['map'](_0x577e64=>{var _0x5cfeab=_0x65040b,_0x1eeeb0,_0x5befcf,_0x2ed490,_0x451332;try{if(!_0x1f963b[_0x5cfeab(0x14a)]){let _0x213503=((_0x5befcf=(_0x1eeeb0=_0x1f963b['process'])==null?void 0x0:_0x1eeeb0[_0x5cfeab(0x184)])==null?void 0x0:_0x5befcf[_0x5cfeab(0x1a1)])||((_0x451332=(_0x2ed490=_0x1f963b[_0x5cfeab(0x1d1)])==null?void 0x0:_0x2ed490[_0x5cfeab(0x16e)])==null?void 0x0:_0x451332[_0x5cfeab(0x163)])===_0x5cfeab(0x199);(_0xf6067d===_0x5cfeab(0x1a7)||_0xf6067d===_0x5cfeab(0x1dd)||_0xf6067d===_0x5cfeab(0x1af)||_0xf6067d===_0x5cfeab(0x153))&&(_0xf6067d+=_0x213503?_0x5cfeab(0x193):_0x5cfeab(0x10c)),_0x1f963b[_0x5cfeab(0x14a)]={'id':+new Date(),'tool':_0xf6067d},_0x473826&&_0xf6067d&&!_0x213503&&console[_0x5cfeab(0x194)](_0x5cfeab(0x17f)+(_0xf6067d['charAt'](0x0)[_0x5cfeab(0x1b1)]()+_0xf6067d['substr'](0x1))+',',_0x5cfeab(0x1b3),'see\\x20https://tinyurl.com/2vt8jxzw\\x20for\\x20more\\x20info.');}let _0x80b835=new Z(_0x1f963b,_0x263322,_0x577e64,_0x43c8cd,_0xf3e79,_0x504ee4);return _0x80b835[_0x5cfeab(0x17e)][_0x5cfeab(0x190)](_0x80b835);}catch(_0x1cb024){return console[_0x5cfeab(0x105)](_0x5cfeab(0x11d),_0x1cb024&&_0x1cb024['message']),()=>{};}});return _0x183d11=>_0x5e33cb[_0x65040b(0x1d8)](_0x404b4f=>_0x404b4f(_0x183d11));}function ie(_0x5a5d6e,_0x3ab594,_0x3d399e,_0x309169){var _0x2b0214=_0x2160de;_0x309169&&_0x5a5d6e===_0x2b0214(0x11b)&&_0x3d399e[_0x2b0214(0x191)][_0x2b0214(0x11b)]();}function _0x21a3(_0x3c4fd7,_0x245abe){var _0x3632c8=_0x3632();return _0x21a3=function(_0x21a3d1,_0x24d2de){_0x21a3d1=_0x21a3d1-0xee;var _0x3406f5=_0x3632c8[_0x21a3d1];return _0x3406f5;},_0x21a3(_0x3c4fd7,_0x245abe);}function B(_0x479ade){var _0x55e4b6=_0x2160de,_0x1db6df,_0x2675fc;let _0x253e83=function(_0x461cb,_0x51aef7){return _0x51aef7-_0x461cb;},_0x24648b;if(_0x479ade[_0x55e4b6(0x12c)])_0x24648b=function(){var _0x28c428=_0x55e4b6;return _0x479ade[_0x28c428(0x12c)][_0x28c428(0x129)]();};else{if(_0x479ade[_0x55e4b6(0x1d1)]&&_0x479ade[_0x55e4b6(0x1d1)][_0x55e4b6(0x148)]&&((_0x2675fc=(_0x1db6df=_0x479ade['process'])==null?void 0x0:_0x1db6df[_0x55e4b6(0x16e)])==null?void 0x0:_0x2675fc[_0x55e4b6(0x163)])!==_0x55e4b6(0x199))_0x24648b=function(){var _0x44dcc4=_0x55e4b6;return _0x479ade[_0x44dcc4(0x1d1)][_0x44dcc4(0x148)]();},_0x253e83=function(_0x1e11c5,_0xa37d54){return 0x3e8*(_0xa37d54[0x0]-_0x1e11c5[0x0])+(_0xa37d54[0x1]-_0x1e11c5[0x1])/0xf4240;};else try{let {performance:_0x568fd6}=require(_0x55e4b6(0x185));_0x24648b=function(){var _0x5f57ed=_0x55e4b6;return _0x568fd6[_0x5f57ed(0x129)]();};}catch{_0x24648b=function(){return+new Date();};}}return{'elapsed':_0x253e83,'timeStamp':_0x24648b,'now':()=>Date['now']()};}function H(_0x10dccb,_0x1e80af,_0x352174){var _0x4997ac=_0x2160de,_0x5b1ba2,_0x11f9c0,_0x27ac93,_0x36b7af,_0x2b4871;if(_0x10dccb[_0x4997ac(0x19d)]!==void 0x0)return _0x10dccb['_consoleNinjaAllowedToStart'];let _0x392b63=((_0x11f9c0=(_0x5b1ba2=_0x10dccb[_0x4997ac(0x1d1)])==null?void 0x0:_0x5b1ba2[_0x4997ac(0x184)])==null?void 0x0:_0x11f9c0[_0x4997ac(0x1a1)])||((_0x36b7af=(_0x27ac93=_0x10dccb[_0x4997ac(0x1d1)])==null?void 0x0:_0x27ac93['env'])==null?void 0x0:_0x36b7af['NEXT_RUNTIME'])===_0x4997ac(0x199);function _0x2d8e02(_0x1544db){var _0x36dbda=_0x4997ac;if(_0x1544db['startsWith']('/')&&_0x1544db[_0x36dbda(0x165)]('/')){let _0x4974fb=new RegExp(_0x1544db[_0x36dbda(0x1ad)](0x1,-0x1));return _0x1401d3=>_0x4974fb[_0x36dbda(0x16f)](_0x1401d3);}else{if(_0x1544db[_0x36dbda(0x198)]('*')||_0x1544db[_0x36dbda(0x198)]('?')){let _0x2cb3b9=new RegExp('^'+_0x1544db[_0x36dbda(0x144)](/\\./g,String[_0x36dbda(0x12f)](0x5c)+'.')['replace'](/\\*/g,'.*')['replace'](/\\?/g,'.')+String['fromCharCode'](0x24));return _0x320b97=>_0x2cb3b9[_0x36dbda(0x16f)](_0x320b97);}else return _0x3d785b=>_0x3d785b===_0x1544db;}}let _0x2bda5a=_0x1e80af['map'](_0x2d8e02);return _0x10dccb['_consoleNinjaAllowedToStart']=_0x392b63||!_0x1e80af,!_0x10dccb[_0x4997ac(0x19d)]&&((_0x2b4871=_0x10dccb[_0x4997ac(0x191)])==null?void 0x0:_0x2b4871[_0x4997ac(0x15a)])&&(_0x10dccb[_0x4997ac(0x19d)]=_0x2bda5a['some'](_0x45fe63=>_0x45fe63(_0x10dccb[_0x4997ac(0x191)][_0x4997ac(0x15a)]))),_0x10dccb[_0x4997ac(0x19d)];}function X(_0x3e77c0,_0x368502,_0x3d2d4c,_0x391fb4){var _0x144a79=_0x2160de;_0x3e77c0=_0x3e77c0,_0x368502=_0x368502,_0x3d2d4c=_0x3d2d4c,_0x391fb4=_0x391fb4;let _0x83c743=B(_0x3e77c0),_0x47a395=_0x83c743[_0x144a79(0xf0)],_0x24d130=_0x83c743['timeStamp'];class _0x4e138c{constructor(){var _0x50df0f=_0x144a79;this['_keyStrRegExp']=/^(?!(?:do|if|in|for|let|new|try|var|case|else|enum|eval|false|null|this|true|void|with|break|catch|class|const|super|throw|while|yield|delete|export|import|public|return|static|switch|typeof|default|extends|finally|package|private|continue|debugger|function|arguments|interface|protected|implements|instanceof)$)[_$a-zA-Z\\xA0-\\uFFFF][_$a-zA-Z0-9\\xA0-\\uFFFF]*$/,this[_0x50df0f(0x108)]=/^(0|[1-9][0-9]*)$/,this[_0x50df0f(0x1b5)]=/'([^\\\\']|\\\\')*'/,this[_0x50df0f(0x168)]=_0x3e77c0[_0x50df0f(0x1a9)],this[_0x50df0f(0x180)]=_0x3e77c0[_0x50df0f(0x157)],this[_0x50df0f(0x1c2)]=Object[_0x50df0f(0x131)],this[_0x50df0f(0x17a)]=Object[_0x50df0f(0x18c)],this[_0x50df0f(0x134)]=_0x3e77c0['Symbol'],this[_0x50df0f(0x1db)]=RegExp[_0x50df0f(0x1e8)]['toString'],this[_0x50df0f(0x18f)]=Date['prototype'][_0x50df0f(0xff)];}[_0x144a79(0x15e)](_0x5416d7,_0x116012,_0x4064e,_0x5d249a){var _0x276822=_0x144a79,_0x49e6d5=this,_0x57b7ae=_0x4064e[_0x276822(0x10b)];function _0x3b3f6a(_0x5d68a0,_0x790290,_0x5d67a7){var _0x395046=_0x276822;_0x790290[_0x395046(0x123)]=_0x395046(0x1b6),_0x790290[_0x395046(0x174)]=_0x5d68a0[_0x395046(0x1ae)],_0x3d70f8=_0x5d67a7['node'][_0x395046(0x1bc)],_0x5d67a7[_0x395046(0x1a1)][_0x395046(0x1bc)]=_0x790290,_0x49e6d5[_0x395046(0xf1)](_0x790290,_0x5d67a7);}try{_0x4064e[_0x276822(0x171)]++,_0x4064e[_0x276822(0x10b)]&&_0x4064e['autoExpandPreviousObjects'][_0x276822(0xf9)](_0x116012);var _0x409809,_0x4b11d6,_0x4c519c,_0x77116f,_0x22b5a1=[],_0x5988bd=[],_0x39ee3b,_0x21d3f8=this['_type'](_0x116012),_0x2d1504=_0x21d3f8===_0x276822(0x12b),_0xb381de=!0x1,_0x2aa0a9=_0x21d3f8==='function',_0x3fdc8a=this[_0x276822(0x1e2)](_0x21d3f8),_0x2795c6=this['_isPrimitiveWrapperType'](_0x21d3f8),_0x5995ba=_0x3fdc8a||_0x2795c6,_0x1134b4={},_0x3e617b=0x0,_0x34b838=!0x1,_0x3d70f8,_0x448392=/^(([1-9]{1}[0-9]*)|0)$/;if(_0x4064e[_0x276822(0x125)]){if(_0x2d1504){if(_0x4b11d6=_0x116012['length'],_0x4b11d6>_0x4064e['elements']){for(_0x4c519c=0x0,_0x77116f=_0x4064e[_0x276822(0x122)],_0x409809=_0x4c519c;_0x409809<_0x77116f;_0x409809++)_0x5988bd['push'](_0x49e6d5[_0x276822(0xf5)](_0x22b5a1,_0x116012,_0x21d3f8,_0x409809,_0x4064e));_0x5416d7[_0x276822(0x1d3)]=!0x0;}else{for(_0x4c519c=0x0,_0x77116f=_0x4b11d6,_0x409809=_0x4c519c;_0x409809<_0x77116f;_0x409809++)_0x5988bd[_0x276822(0xf9)](_0x49e6d5[_0x276822(0xf5)](_0x22b5a1,_0x116012,_0x21d3f8,_0x409809,_0x4064e));}_0x4064e[_0x276822(0x1b8)]+=_0x5988bd[_0x276822(0x13f)];}if(!(_0x21d3f8===_0x276822(0x1ce)||_0x21d3f8==='undefined')&&!_0x3fdc8a&&_0x21d3f8!==_0x276822(0xf3)&&_0x21d3f8!==_0x276822(0x14b)&&_0x21d3f8!==_0x276822(0x1e1)){var _0x5bbc59=_0x5d249a[_0x276822(0x109)]||_0x4064e['props'];if(this['_isSet'](_0x116012)?(_0x409809=0x0,_0x116012[_0x276822(0x1d8)](function(_0x370926){var _0x37eb0d=_0x276822;if(_0x3e617b++,_0x4064e[_0x37eb0d(0x1b8)]++,_0x3e617b>_0x5bbc59){_0x34b838=!0x0;return;}if(!_0x4064e[_0x37eb0d(0x1c0)]&&_0x4064e[_0x37eb0d(0x10b)]&&_0x4064e['autoExpandPropertyCount']>_0x4064e[_0x37eb0d(0x14e)]){_0x34b838=!0x0;return;}_0x5988bd['push'](_0x49e6d5['_addProperty'](_0x22b5a1,_0x116012,_0x37eb0d(0xfc),_0x409809++,_0x4064e,function(_0x1b010a){return function(){return _0x1b010a;};}(_0x370926)));})):this[_0x276822(0x176)](_0x116012)&&_0x116012['forEach'](function(_0x3e9664,_0x283dc1){var _0x6b9f9b=_0x276822;if(_0x3e617b++,_0x4064e['autoExpandPropertyCount']++,_0x3e617b>_0x5bbc59){_0x34b838=!0x0;return;}if(!_0x4064e[_0x6b9f9b(0x1c0)]&&_0x4064e[_0x6b9f9b(0x10b)]&&_0x4064e[_0x6b9f9b(0x1b8)]>_0x4064e[_0x6b9f9b(0x14e)]){_0x34b838=!0x0;return;}var _0x42447e=_0x283dc1[_0x6b9f9b(0xff)]();_0x42447e[_0x6b9f9b(0x13f)]>0x64&&(_0x42447e=_0x42447e[_0x6b9f9b(0x1ad)](0x0,0x64)+_0x6b9f9b(0x143)),_0x5988bd['push'](_0x49e6d5['_addProperty'](_0x22b5a1,_0x116012,'Map',_0x42447e,_0x4064e,function(_0x490eaf){return function(){return _0x490eaf;};}(_0x3e9664)));}),!_0xb381de){try{for(_0x39ee3b in _0x116012)if(!(_0x2d1504&&_0x448392['test'](_0x39ee3b))&&!this[_0x276822(0x151)](_0x116012,_0x39ee3b,_0x4064e)){if(_0x3e617b++,_0x4064e[_0x276822(0x1b8)]++,_0x3e617b>_0x5bbc59){_0x34b838=!0x0;break;}if(!_0x4064e[_0x276822(0x1c0)]&&_0x4064e['autoExpand']&&_0x4064e[_0x276822(0x1b8)]>_0x4064e[_0x276822(0x14e)]){_0x34b838=!0x0;break;}_0x5988bd[_0x276822(0xf9)](_0x49e6d5[_0x276822(0x117)](_0x22b5a1,_0x1134b4,_0x116012,_0x21d3f8,_0x39ee3b,_0x4064e));}}catch{}if(_0x1134b4['_p_length']=!0x0,_0x2aa0a9&&(_0x1134b4[_0x276822(0x1a6)]=!0x0),!_0x34b838){var _0x46fabb=[][_0x276822(0x1a5)](this[_0x276822(0x17a)](_0x116012))[_0x276822(0x1a5)](this['_getOwnPropertySymbols'](_0x116012));for(_0x409809=0x0,_0x4b11d6=_0x46fabb['length'];_0x409809<_0x4b11d6;_0x409809++)if(_0x39ee3b=_0x46fabb[_0x409809],!(_0x2d1504&&_0x448392[_0x276822(0x16f)](_0x39ee3b[_0x276822(0xff)]()))&&!this[_0x276822(0x151)](_0x116012,_0x39ee3b,_0x4064e)&&!_0x1134b4[_0x276822(0x162)+_0x39ee3b[_0x276822(0xff)]()]){if(_0x3e617b++,_0x4064e['autoExpandPropertyCount']++,_0x3e617b>_0x5bbc59){_0x34b838=!0x0;break;}if(!_0x4064e['isExpressionToEvaluate']&&_0x4064e[_0x276822(0x10b)]&&_0x4064e['autoExpandPropertyCount']>_0x4064e[_0x276822(0x14e)]){_0x34b838=!0x0;break;}_0x5988bd[_0x276822(0xf9)](_0x49e6d5[_0x276822(0x117)](_0x22b5a1,_0x1134b4,_0x116012,_0x21d3f8,_0x39ee3b,_0x4064e));}}}}}if(_0x5416d7[_0x276822(0x123)]=_0x21d3f8,_0x5995ba?(_0x5416d7[_0x276822(0x118)]=_0x116012[_0x276822(0x1ca)](),this[_0x276822(0x1a3)](_0x21d3f8,_0x5416d7,_0x4064e,_0x5d249a)):_0x21d3f8===_0x276822(0x18e)?_0x5416d7[_0x276822(0x118)]=this[_0x276822(0x18f)]['call'](_0x116012):_0x21d3f8===_0x276822(0x1e1)?_0x5416d7[_0x276822(0x118)]=_0x116012[_0x276822(0xff)]():_0x21d3f8===_0x276822(0x1d9)?_0x5416d7[_0x276822(0x118)]=this[_0x276822(0x1db)][_0x276822(0x12d)](_0x116012):_0x21d3f8===_0x276822(0xfb)&&this['_Symbol']?_0x5416d7[_0x276822(0x118)]=this[_0x276822(0x134)]['prototype']['toString'][_0x276822(0x12d)](_0x116012):!_0x4064e[_0x276822(0x125)]&&!(_0x21d3f8===_0x276822(0x1ce)||_0x21d3f8===_0x276822(0x1a9))&&(delete _0x5416d7[_0x276822(0x118)],_0x5416d7[_0x276822(0x15d)]=!0x0),_0x34b838&&(_0x5416d7[_0x276822(0x13b)]=!0x0),_0x3d70f8=_0x4064e[_0x276822(0x1a1)][_0x276822(0x1bc)],_0x4064e[_0x276822(0x1a1)]['current']=_0x5416d7,this[_0x276822(0xf1)](_0x5416d7,_0x4064e),_0x5988bd[_0x276822(0x13f)]){for(_0x409809=0x0,_0x4b11d6=_0x5988bd[_0x276822(0x13f)];_0x409809<_0x4b11d6;_0x409809++)_0x5988bd[_0x409809](_0x409809);}_0x22b5a1[_0x276822(0x13f)]&&(_0x5416d7[_0x276822(0x109)]=_0x22b5a1);}catch(_0x545cfc){_0x3b3f6a(_0x545cfc,_0x5416d7,_0x4064e);}return this[_0x276822(0x132)](_0x116012,_0x5416d7),this[_0x276822(0x103)](_0x5416d7,_0x4064e),_0x4064e['node'][_0x276822(0x1bc)]=_0x3d70f8,_0x4064e[_0x276822(0x171)]--,_0x4064e[_0x276822(0x10b)]=_0x57b7ae,_0x4064e['autoExpand']&&_0x4064e[_0x276822(0x169)][_0x276822(0x1cb)](),_0x5416d7;}[_0x144a79(0x1bf)](_0x342ecf){return Object['getOwnPropertySymbols']?Object['getOwnPropertySymbols'](_0x342ecf):[];}[_0x144a79(0x1c4)](_0x5ba2ae){var _0x5a7687=_0x144a79;return!!(_0x5ba2ae&&_0x3e77c0[_0x5a7687(0xfc)]&&this['_objectToString'](_0x5ba2ae)===_0x5a7687(0x1e0)&&_0x5ba2ae[_0x5a7687(0x1d8)]);}[_0x144a79(0x151)](_0x1cb176,_0x21f6ff,_0x22cda5){var _0x223b84=_0x144a79;return _0x22cda5[_0x223b84(0x11a)]?typeof _0x1cb176[_0x21f6ff]==_0x223b84(0x1df):!0x1;}[_0x144a79(0x1b9)](_0x14fbf5){var _0x540dfe=_0x144a79,_0x21d37e='';return _0x21d37e=typeof _0x14fbf5,_0x21d37e===_0x540dfe(0xf2)?this['_objectToString'](_0x14fbf5)===_0x540dfe(0xef)?_0x21d37e=_0x540dfe(0x12b):this[_0x540dfe(0x1a4)](_0x14fbf5)===_0x540dfe(0x1d0)?_0x21d37e='date':this[_0x540dfe(0x1a4)](_0x14fbf5)===_0x540dfe(0x179)?_0x21d37e=_0x540dfe(0x1e1):_0x14fbf5===null?_0x21d37e=_0x540dfe(0x1ce):_0x14fbf5[_0x540dfe(0x10e)]&&(_0x21d37e=_0x14fbf5['constructor'][_0x540dfe(0xfe)]||_0x21d37e):_0x21d37e===_0x540dfe(0x1a9)&&this[_0x540dfe(0x180)]&&_0x14fbf5 instanceof this['_HTMLAllCollection']&&(_0x21d37e=_0x540dfe(0x157)),_0x21d37e;}[_0x144a79(0x1a4)](_0x8e2c33){var _0x252277=_0x144a79;return Object[_0x252277(0x1e8)]['toString'][_0x252277(0x12d)](_0x8e2c33);}[_0x144a79(0x1e2)](_0x575741){var _0x23d891=_0x144a79;return _0x575741===_0x23d891(0x18a)||_0x575741==='string'||_0x575741===_0x23d891(0x133);}[_0x144a79(0x101)](_0x4456b7){var _0x2de837=_0x144a79;return _0x4456b7===_0x2de837(0x140)||_0x4456b7==='String'||_0x4456b7===_0x2de837(0x135);}[_0x144a79(0xf5)](_0x4354f5,_0x2e6613,_0x5874b5,_0x561ca8,_0x22e9fa,_0xf7af1c){var _0x1cf564=this;return function(_0x384940){var _0x598128=_0x21a3,_0x1c7c0b=_0x22e9fa[_0x598128(0x1a1)][_0x598128(0x1bc)],_0x46c00c=_0x22e9fa[_0x598128(0x1a1)][_0x598128(0x10a)],_0x16be0b=_0x22e9fa[_0x598128(0x1a1)][_0x598128(0x11c)];_0x22e9fa[_0x598128(0x1a1)][_0x598128(0x11c)]=_0x1c7c0b,_0x22e9fa[_0x598128(0x1a1)][_0x598128(0x10a)]=typeof _0x561ca8=='number'?_0x561ca8:_0x384940,_0x4354f5[_0x598128(0xf9)](_0x1cf564['_property'](_0x2e6613,_0x5874b5,_0x561ca8,_0x22e9fa,_0xf7af1c)),_0x22e9fa[_0x598128(0x1a1)][_0x598128(0x11c)]=_0x16be0b,_0x22e9fa['node']['index']=_0x46c00c;};}[_0x144a79(0x117)](_0x58d056,_0x3f3d81,_0x423f15,_0x7eaa6e,_0xd8e80,_0x57780f,_0x4dffbc){var _0x32a146=_0x144a79,_0x41efa8=this;return _0x3f3d81[_0x32a146(0x162)+_0xd8e80['toString']()]=!0x0,function(_0x4d920b){var _0x49b5c2=_0x32a146,_0x5ac13c=_0x57780f[_0x49b5c2(0x1a1)][_0x49b5c2(0x1bc)],_0x3b2e7f=_0x57780f['node'][_0x49b5c2(0x10a)],_0x188cf3=_0x57780f[_0x49b5c2(0x1a1)]['parent'];_0x57780f[_0x49b5c2(0x1a1)][_0x49b5c2(0x11c)]=_0x5ac13c,_0x57780f['node'][_0x49b5c2(0x10a)]=_0x4d920b,_0x58d056[_0x49b5c2(0xf9)](_0x41efa8[_0x49b5c2(0x11e)](_0x423f15,_0x7eaa6e,_0xd8e80,_0x57780f,_0x4dffbc)),_0x57780f[_0x49b5c2(0x1a1)][_0x49b5c2(0x11c)]=_0x188cf3,_0x57780f[_0x49b5c2(0x1a1)][_0x49b5c2(0x10a)]=_0x3b2e7f;};}[_0x144a79(0x11e)](_0x30d594,_0x313c60,_0x116b3f,_0x110528,_0x4c9ac0){var _0x860bad=_0x144a79,_0x3c61c2=this;_0x4c9ac0||(_0x4c9ac0=function(_0x374fcf,_0x341fdd){return _0x374fcf[_0x341fdd];});var _0x5ebc8c=_0x116b3f[_0x860bad(0xff)](),_0x32f673=_0x110528[_0x860bad(0x181)]||{},_0x38968e=_0x110528['depth'],_0xd7277d=_0x110528[_0x860bad(0x1c0)];try{var _0x14b85f=this['_isMap'](_0x30d594),_0x316f78=_0x5ebc8c;_0x14b85f&&_0x316f78[0x0]==='\\x27'&&(_0x316f78=_0x316f78[_0x860bad(0x112)](0x1,_0x316f78['length']-0x2));var _0x4099d8=_0x110528[_0x860bad(0x181)]=_0x32f673['_p_'+_0x316f78];_0x4099d8&&(_0x110528[_0x860bad(0x125)]=_0x110528[_0x860bad(0x125)]+0x1),_0x110528[_0x860bad(0x1c0)]=!!_0x4099d8;var _0x28f7fd=typeof _0x116b3f==_0x860bad(0xfb),_0x33a0d0={'name':_0x28f7fd||_0x14b85f?_0x5ebc8c:this[_0x860bad(0x1cf)](_0x5ebc8c)};if(_0x28f7fd&&(_0x33a0d0[_0x860bad(0xfb)]=!0x0),!(_0x313c60===_0x860bad(0x12b)||_0x313c60==='Error')){var _0x1db4a5=this[_0x860bad(0x1c2)](_0x30d594,_0x116b3f);if(_0x1db4a5&&(_0x1db4a5[_0x860bad(0x188)]&&(_0x33a0d0[_0x860bad(0x1d4)]=!0x0),_0x1db4a5[_0x860bad(0x136)]&&!_0x4099d8&&!_0x110528[_0x860bad(0x17b)]))return _0x33a0d0[_0x860bad(0x11f)]=!0x0,this['_processTreeNodeResult'](_0x33a0d0,_0x110528),_0x33a0d0;}var _0x45eef2;try{_0x45eef2=_0x4c9ac0(_0x30d594,_0x116b3f);}catch(_0x349276){return _0x33a0d0={'name':_0x5ebc8c,'type':'unknown','error':_0x349276[_0x860bad(0x1ae)]},this[_0x860bad(0x120)](_0x33a0d0,_0x110528),_0x33a0d0;}var _0x539394=this[_0x860bad(0x1b9)](_0x45eef2),_0x490c82=this[_0x860bad(0x1e2)](_0x539394);if(_0x33a0d0[_0x860bad(0x123)]=_0x539394,_0x490c82)this[_0x860bad(0x120)](_0x33a0d0,_0x110528,_0x45eef2,function(){var _0x5a3749=_0x860bad;_0x33a0d0[_0x5a3749(0x118)]=_0x45eef2['valueOf'](),!_0x4099d8&&_0x3c61c2[_0x5a3749(0x1a3)](_0x539394,_0x33a0d0,_0x110528,{});});else{var _0x3ce9b1=_0x110528['autoExpand']&&_0x110528[_0x860bad(0x171)]<_0x110528[_0x860bad(0x1cd)]&&_0x110528[_0x860bad(0x169)][_0x860bad(0x111)](_0x45eef2)<0x0&&_0x539394!=='function'&&_0x110528['autoExpandPropertyCount']<_0x110528[_0x860bad(0x14e)];_0x3ce9b1||_0x110528[_0x860bad(0x171)]<_0x38968e||_0x4099d8?(this[_0x860bad(0x15e)](_0x33a0d0,_0x45eef2,_0x110528,_0x4099d8||{}),this[_0x860bad(0x132)](_0x45eef2,_0x33a0d0)):this[_0x860bad(0x120)](_0x33a0d0,_0x110528,_0x45eef2,function(){var _0x4e871a=_0x860bad;_0x539394===_0x4e871a(0x1ce)||_0x539394===_0x4e871a(0x1a9)||(delete _0x33a0d0[_0x4e871a(0x118)],_0x33a0d0['capped']=!0x0);});}return _0x33a0d0;}finally{_0x110528[_0x860bad(0x181)]=_0x32f673,_0x110528['depth']=_0x38968e,_0x110528[_0x860bad(0x1c0)]=_0xd7277d;}}[_0x144a79(0x1a3)](_0x48037b,_0x2f1dd0,_0x473c43,_0x2ec293){var _0x39eca7=_0x144a79,_0x3f171f=_0x2ec293[_0x39eca7(0x139)]||_0x473c43[_0x39eca7(0x139)];if((_0x48037b==='string'||_0x48037b===_0x39eca7(0xf3))&&_0x2f1dd0[_0x39eca7(0x118)]){let _0x2d465d=_0x2f1dd0[_0x39eca7(0x118)][_0x39eca7(0x13f)];_0x473c43[_0x39eca7(0x178)]+=_0x2d465d,_0x473c43[_0x39eca7(0x178)]>_0x473c43[_0x39eca7(0x1d2)]?(_0x2f1dd0[_0x39eca7(0x15d)]='',delete _0x2f1dd0['value']):_0x2d465d>_0x3f171f&&(_0x2f1dd0[_0x39eca7(0x15d)]=_0x2f1dd0[_0x39eca7(0x118)][_0x39eca7(0x112)](0x0,_0x3f171f),delete _0x2f1dd0[_0x39eca7(0x118)]);}}[_0x144a79(0x176)](_0x5e12a9){var _0x2e6805=_0x144a79;return!!(_0x5e12a9&&_0x3e77c0[_0x2e6805(0x149)]&&this[_0x2e6805(0x1a4)](_0x5e12a9)===_0x2e6805(0x1e6)&&_0x5e12a9[_0x2e6805(0x1d8)]);}[_0x144a79(0x1cf)](_0x45972a){var _0x58ae9d=_0x144a79;if(_0x45972a[_0x58ae9d(0x19b)](/^\\d+$/))return _0x45972a;var _0x319850;try{_0x319850=JSON[_0x58ae9d(0x1bb)](''+_0x45972a);}catch{_0x319850='\\x22'+this[_0x58ae9d(0x1a4)](_0x45972a)+'\\x22';}return _0x319850[_0x58ae9d(0x19b)](/^\"([a-zA-Z_][a-zA-Z_0-9]*)\"$/)?_0x319850=_0x319850[_0x58ae9d(0x112)](0x1,_0x319850['length']-0x2):_0x319850=_0x319850['replace'](/'/g,'\\x5c\\x27')[_0x58ae9d(0x144)](/\\\\\"/g,'\\x22')[_0x58ae9d(0x144)](/(^\"|\"$)/g,'\\x27'),_0x319850;}[_0x144a79(0x120)](_0x1bd8dc,_0x2ed4bd,_0x324def,_0x4e2bae){var _0x357ebc=_0x144a79;this[_0x357ebc(0xf1)](_0x1bd8dc,_0x2ed4bd),_0x4e2bae&&_0x4e2bae(),this[_0x357ebc(0x132)](_0x324def,_0x1bd8dc),this[_0x357ebc(0x103)](_0x1bd8dc,_0x2ed4bd);}['_treeNodePropertiesBeforeFullValue'](_0x107d65,_0x3ed7ca){var _0x219b16=_0x144a79;this['_setNodeId'](_0x107d65,_0x3ed7ca),this[_0x219b16(0x124)](_0x107d65,_0x3ed7ca),this[_0x219b16(0x1ac)](_0x107d65,_0x3ed7ca),this[_0x219b16(0x110)](_0x107d65,_0x3ed7ca);}[_0x144a79(0x147)](_0x56ce50,_0x58285a){}[_0x144a79(0x124)](_0x2fd389,_0x113b19){}[_0x144a79(0x10d)](_0x46fedc,_0x1fb3d1){}[_0x144a79(0x1c8)](_0x53d6a4){var _0x314f4b=_0x144a79;return _0x53d6a4===this[_0x314f4b(0x168)];}[_0x144a79(0x103)](_0x94f0d4,_0x1f78a2){var _0xc416c9=_0x144a79;this[_0xc416c9(0x10d)](_0x94f0d4,_0x1f78a2),this['_setNodeExpandableState'](_0x94f0d4),_0x1f78a2['sortProps']&&this[_0xc416c9(0xee)](_0x94f0d4),this[_0xc416c9(0x100)](_0x94f0d4,_0x1f78a2),this[_0xc416c9(0x19a)](_0x94f0d4,_0x1f78a2),this[_0xc416c9(0x115)](_0x94f0d4);}[_0x144a79(0x132)](_0x912632,_0x5f5544){var _0x20b522=_0x144a79;let _0x50f594;try{_0x3e77c0[_0x20b522(0x154)]&&(_0x50f594=_0x3e77c0[_0x20b522(0x154)][_0x20b522(0x174)],_0x3e77c0[_0x20b522(0x154)][_0x20b522(0x174)]=function(){}),_0x912632&&typeof _0x912632['length']==_0x20b522(0x133)&&(_0x5f5544['length']=_0x912632['length']);}catch{}finally{_0x50f594&&(_0x3e77c0[_0x20b522(0x154)]['error']=_0x50f594);}if(_0x5f5544[_0x20b522(0x123)]==='number'||_0x5f5544[_0x20b522(0x123)]===_0x20b522(0x135)){if(isNaN(_0x5f5544[_0x20b522(0x118)]))_0x5f5544[_0x20b522(0x158)]=!0x0,delete _0x5f5544['value'];else switch(_0x5f5544[_0x20b522(0x118)]){case Number['POSITIVE_INFINITY']:_0x5f5544[_0x20b522(0x186)]=!0x0,delete _0x5f5544[_0x20b522(0x118)];break;case Number[_0x20b522(0x116)]:_0x5f5544[_0x20b522(0x1aa)]=!0x0,delete _0x5f5544[_0x20b522(0x118)];break;case 0x0:this[_0x20b522(0x141)](_0x5f5544[_0x20b522(0x118)])&&(_0x5f5544[_0x20b522(0x14c)]=!0x0);break;}}else _0x5f5544[_0x20b522(0x123)]===_0x20b522(0x1df)&&typeof _0x912632['name']==_0x20b522(0x13d)&&_0x912632[_0x20b522(0xfe)]&&_0x5f5544[_0x20b522(0xfe)]&&_0x912632[_0x20b522(0xfe)]!==_0x5f5544['name']&&(_0x5f5544[_0x20b522(0x196)]=_0x912632['name']);}[_0x144a79(0x141)](_0x3d8de7){var _0x5bd0fa=_0x144a79;return 0x1/_0x3d8de7===Number[_0x5bd0fa(0x116)];}['_sortProps'](_0x4aa6dd){var _0x119666=_0x144a79;!_0x4aa6dd[_0x119666(0x109)]||!_0x4aa6dd[_0x119666(0x109)][_0x119666(0x13f)]||_0x4aa6dd[_0x119666(0x123)]===_0x119666(0x12b)||_0x4aa6dd[_0x119666(0x123)]===_0x119666(0x149)||_0x4aa6dd[_0x119666(0x123)]===_0x119666(0xfc)||_0x4aa6dd[_0x119666(0x109)]['sort'](function(_0x31ad1b,_0x1e1fbf){var _0x2d660e=_0x119666,_0x1d39c7=_0x31ad1b[_0x2d660e(0xfe)][_0x2d660e(0x1ab)](),_0x58bbc5=_0x1e1fbf['name'][_0x2d660e(0x1ab)]();return _0x1d39c7<_0x58bbc5?-0x1:_0x1d39c7>_0x58bbc5?0x1:0x0;});}[_0x144a79(0x100)](_0x256f87,_0x39cae1){var _0x3d0616=_0x144a79;if(!(_0x39cae1[_0x3d0616(0x11a)]||!_0x256f87['props']||!_0x256f87['props'][_0x3d0616(0x13f)])){for(var _0x12a78b=[],_0x11c382=[],_0x564eb4=0x0,_0x128043=_0x256f87['props'][_0x3d0616(0x13f)];_0x564eb4<_0x128043;_0x564eb4++){var _0x5b20c3=_0x256f87[_0x3d0616(0x109)][_0x564eb4];_0x5b20c3[_0x3d0616(0x123)]==='function'?_0x12a78b[_0x3d0616(0xf9)](_0x5b20c3):_0x11c382[_0x3d0616(0xf9)](_0x5b20c3);}if(!(!_0x11c382[_0x3d0616(0x13f)]||_0x12a78b['length']<=0x1)){_0x256f87[_0x3d0616(0x109)]=_0x11c382;var _0x32daf8={'functionsNode':!0x0,'props':_0x12a78b};this[_0x3d0616(0x147)](_0x32daf8,_0x39cae1),this[_0x3d0616(0x10d)](_0x32daf8,_0x39cae1),this['_setNodeExpandableState'](_0x32daf8),this[_0x3d0616(0x110)](_0x32daf8,_0x39cae1),_0x32daf8['id']+='\\x20f',_0x256f87[_0x3d0616(0x109)][_0x3d0616(0x1b7)](_0x32daf8);}}}[_0x144a79(0x19a)](_0x15acc0,_0x155efd){}[_0x144a79(0x161)](_0x2e282a){}[_0x144a79(0x18b)](_0x237103){var _0xfedee4=_0x144a79;return Array[_0xfedee4(0x164)](_0x237103)||typeof _0x237103==_0xfedee4(0xf2)&&this['_objectToString'](_0x237103)===_0xfedee4(0xef);}[_0x144a79(0x110)](_0x93d6b1,_0x555532){}[_0x144a79(0x115)](_0x2328ec){var _0x443c04=_0x144a79;delete _0x2328ec[_0x443c04(0x1c9)],delete _0x2328ec[_0x443c04(0x106)],delete _0x2328ec[_0x443c04(0x1c6)];}[_0x144a79(0x1ac)](_0x473e8a,_0x1f3de2){}}let _0x182186=new _0x4e138c(),_0x4c7196={'props':0x64,'elements':0x64,'strLength':0x400*0x32,'totalStrLength':0x400*0x32,'autoExpandLimit':0x1388,'autoExpandMaxDepth':0xa},_0x3701fa={'props':0x5,'elements':0x5,'strLength':0x100,'totalStrLength':0x100*0x3,'autoExpandLimit':0x1e,'autoExpandMaxDepth':0x2};function _0x213e3e(_0x2ff700,_0x1028d3,_0x6237b8,_0x3512a1,_0xcb090,_0x3b6c4b){var _0x407e31=_0x144a79;let _0x212837,_0x4e347f;try{_0x4e347f=_0x24d130(),_0x212837=_0x3d2d4c[_0x1028d3],!_0x212837||_0x4e347f-_0x212837['ts']>0x1f4&&_0x212837[_0x407e31(0x197)]&&_0x212837[_0x407e31(0x146)]/_0x212837[_0x407e31(0x197)]<0x64?(_0x3d2d4c[_0x1028d3]=_0x212837={'count':0x0,'time':0x0,'ts':_0x4e347f},_0x3d2d4c[_0x407e31(0x1c7)]={}):_0x4e347f-_0x3d2d4c['hits']['ts']>0x32&&_0x3d2d4c[_0x407e31(0x1c7)][_0x407e31(0x197)]&&_0x3d2d4c['hits'][_0x407e31(0x146)]/_0x3d2d4c[_0x407e31(0x1c7)]['count']<0x64&&(_0x3d2d4c[_0x407e31(0x1c7)]={});let _0x36a3f2=[],_0x24554b=_0x212837[_0x407e31(0x102)]||_0x3d2d4c[_0x407e31(0x1c7)][_0x407e31(0x102)]?_0x3701fa:_0x4c7196,_0x347fd7=_0x261a7d=>{var _0x3edcb4=_0x407e31;let _0x3a2713={};return _0x3a2713['props']=_0x261a7d['props'],_0x3a2713[_0x3edcb4(0x122)]=_0x261a7d[_0x3edcb4(0x122)],_0x3a2713[_0x3edcb4(0x139)]=_0x261a7d[_0x3edcb4(0x139)],_0x3a2713[_0x3edcb4(0x1d2)]=_0x261a7d[_0x3edcb4(0x1d2)],_0x3a2713[_0x3edcb4(0x14e)]=_0x261a7d[_0x3edcb4(0x14e)],_0x3a2713[_0x3edcb4(0x1cd)]=_0x261a7d[_0x3edcb4(0x1cd)],_0x3a2713[_0x3edcb4(0xf6)]=!0x1,_0x3a2713[_0x3edcb4(0x11a)]=!_0x368502,_0x3a2713[_0x3edcb4(0x125)]=0x1,_0x3a2713[_0x3edcb4(0x171)]=0x0,_0x3a2713[_0x3edcb4(0x15b)]=_0x3edcb4(0x1dc),_0x3a2713[_0x3edcb4(0x1a0)]='root_exp',_0x3a2713[_0x3edcb4(0x10b)]=!0x0,_0x3a2713['autoExpandPreviousObjects']=[],_0x3a2713[_0x3edcb4(0x1b8)]=0x0,_0x3a2713[_0x3edcb4(0x17b)]=!0x0,_0x3a2713[_0x3edcb4(0x178)]=0x0,_0x3a2713[_0x3edcb4(0x1a1)]={'current':void 0x0,'parent':void 0x0,'index':0x0},_0x3a2713;};for(var _0x25383a=0x0;_0x25383a<_0xcb090[_0x407e31(0x13f)];_0x25383a++)_0x36a3f2[_0x407e31(0xf9)](_0x182186['serialize']({'timeNode':_0x2ff700===_0x407e31(0x146)||void 0x0},_0xcb090[_0x25383a],_0x347fd7(_0x24554b),{}));if(_0x2ff700==='trace'||_0x2ff700===_0x407e31(0x174)){let _0x5a1a14=Error[_0x407e31(0xfa)];try{Error[_0x407e31(0xfa)]=0x1/0x0,_0x36a3f2[_0x407e31(0xf9)](_0x182186[_0x407e31(0x15e)]({'stackNode':!0x0},new Error()[_0x407e31(0x15f)],_0x347fd7(_0x24554b),{'strLength':0x1/0x0}));}finally{Error[_0x407e31(0xfa)]=_0x5a1a14;}}return{'method':_0x407e31(0x194),'version':_0x391fb4,'args':[{'ts':_0x6237b8,'session':_0x3512a1,'args':_0x36a3f2,'id':_0x1028d3,'context':_0x3b6c4b}]};}catch(_0x5db04b){return{'method':_0x407e31(0x194),'version':_0x391fb4,'args':[{'ts':_0x6237b8,'session':_0x3512a1,'args':[{'type':_0x407e31(0x1b6),'error':_0x5db04b&&_0x5db04b[_0x407e31(0x1ae)]}],'id':_0x1028d3,'context':_0x3b6c4b}]};}finally{try{if(_0x212837&&_0x4e347f){let _0x45e299=_0x24d130();_0x212837['count']++,_0x212837[_0x407e31(0x146)]+=_0x47a395(_0x4e347f,_0x45e299),_0x212837['ts']=_0x45e299,_0x3d2d4c[_0x407e31(0x1c7)][_0x407e31(0x197)]++,_0x3d2d4c[_0x407e31(0x1c7)][_0x407e31(0x146)]+=_0x47a395(_0x4e347f,_0x45e299),_0x3d2d4c[_0x407e31(0x1c7)]['ts']=_0x45e299,(_0x212837[_0x407e31(0x197)]>0x32||_0x212837[_0x407e31(0x146)]>0x64)&&(_0x212837['reduceLimits']=!0x0),(_0x3d2d4c[_0x407e31(0x1c7)][_0x407e31(0x197)]>0x3e8||_0x3d2d4c[_0x407e31(0x1c7)]['time']>0x12c)&&(_0x3d2d4c[_0x407e31(0x1c7)]['reduceLimits']=!0x0);}}catch{}}}return _0x213e3e;}((_0x5d65e9,_0x571af9,_0x37e1e4,_0x433d48,_0x1b02f6,_0x430083,_0x5432d8,_0x102dcb,_0x493a3c,_0x240da5,_0x2821b9)=>{var _0x556dd6=_0x2160de;if(_0x5d65e9[_0x556dd6(0x14d)])return _0x5d65e9[_0x556dd6(0x14d)];if(!H(_0x5d65e9,_0x102dcb,_0x1b02f6))return _0x5d65e9[_0x556dd6(0x14d)]={'consoleLog':()=>{},'consoleTrace':()=>{},'consoleTime':()=>{},'consoleTimeEnd':()=>{},'autoLog':()=>{},'autoLogMany':()=>{},'autoTraceMany':()=>{},'coverage':()=>{},'autoTrace':()=>{},'autoTime':()=>{},'autoTimeEnd':()=>{}},_0x5d65e9['_console_ninja'];let _0x1d6340=B(_0x5d65e9),_0x397ff6=_0x1d6340['elapsed'],_0x1a91da=_0x1d6340['timeStamp'],_0xa22aa9=_0x1d6340[_0x556dd6(0x129)],_0x4dd46f={'hits':{},'ts':{}},_0x22fa43=X(_0x5d65e9,_0x493a3c,_0x4dd46f,_0x430083),_0x590e5e=_0x44c539=>{_0x4dd46f['ts'][_0x44c539]=_0x1a91da();},_0x1ecc6b=(_0x392121,_0x1d046a)=>{let _0x489bc2=_0x4dd46f['ts'][_0x1d046a];if(delete _0x4dd46f['ts'][_0x1d046a],_0x489bc2){let _0x346578=_0x397ff6(_0x489bc2,_0x1a91da());_0x37b780(_0x22fa43('time',_0x392121,_0xa22aa9(),_0x4ee1c5,[_0x346578],_0x1d046a));}},_0x11ac60=_0x3097ff=>{var _0x43212c=_0x556dd6,_0x5de73e;return _0x1b02f6===_0x43212c(0x1a7)&&_0x5d65e9[_0x43212c(0xf8)]&&((_0x5de73e=_0x3097ff==null?void 0x0:_0x3097ff['args'])==null?void 0x0:_0x5de73e['length'])&&(_0x3097ff[_0x43212c(0x114)][0x0]['origin']=_0x5d65e9[_0x43212c(0xf8)]),_0x3097ff;};_0x5d65e9[_0x556dd6(0x14d)]={'consoleLog':(_0x3df3db,_0x1bb5ab)=>{var _0x2864ec=_0x556dd6;_0x5d65e9[_0x2864ec(0x154)][_0x2864ec(0x194)][_0x2864ec(0xfe)]!==_0x2864ec(0x119)&&_0x37b780(_0x22fa43(_0x2864ec(0x194),_0x3df3db,_0xa22aa9(),_0x4ee1c5,_0x1bb5ab));},'consoleTrace':(_0x29da49,_0x3f8374)=>{var _0x280bf8=_0x556dd6,_0x2254c1,_0x1e4130;_0x5d65e9['console'][_0x280bf8(0x194)][_0x280bf8(0xfe)]!=='disabledTrace'&&((_0x1e4130=(_0x2254c1=_0x5d65e9[_0x280bf8(0x1d1)])==null?void 0x0:_0x2254c1['versions'])!=null&&_0x1e4130[_0x280bf8(0x1a1)]&&(_0x5d65e9[_0x280bf8(0x1de)]=!0x0),_0x37b780(_0x11ac60(_0x22fa43(_0x280bf8(0x170),_0x29da49,_0xa22aa9(),_0x4ee1c5,_0x3f8374))));},'consoleError':(_0x547f0a,_0x50dbc7)=>{var _0x3597a9=_0x556dd6;_0x5d65e9[_0x3597a9(0x1de)]=!0x0,_0x37b780(_0x11ac60(_0x22fa43('error',_0x547f0a,_0xa22aa9(),_0x4ee1c5,_0x50dbc7)));},'consoleTime':_0x123ffe=>{_0x590e5e(_0x123ffe);},'consoleTimeEnd':(_0x2171a0,_0x403bb8)=>{_0x1ecc6b(_0x403bb8,_0x2171a0);},'autoLog':(_0x4998d0,_0x1bac37)=>{var _0xd73d23=_0x556dd6;_0x37b780(_0x22fa43(_0xd73d23(0x194),_0x1bac37,_0xa22aa9(),_0x4ee1c5,[_0x4998d0]));},'autoLogMany':(_0x431d18,_0x36d175)=>{var _0x73d5fc=_0x556dd6;_0x37b780(_0x22fa43(_0x73d5fc(0x194),_0x431d18,_0xa22aa9(),_0x4ee1c5,_0x36d175));},'autoTrace':(_0x459c49,_0x27ff2b)=>{var _0x433b06=_0x556dd6;_0x37b780(_0x11ac60(_0x22fa43(_0x433b06(0x170),_0x27ff2b,_0xa22aa9(),_0x4ee1c5,[_0x459c49])));},'autoTraceMany':(_0x2ff4f7,_0x18ce27)=>{_0x37b780(_0x11ac60(_0x22fa43('trace',_0x2ff4f7,_0xa22aa9(),_0x4ee1c5,_0x18ce27)));},'autoTime':(_0x2e41bc,_0x5eb8ec,_0x1c38dd)=>{_0x590e5e(_0x1c38dd);},'autoTimeEnd':(_0x47c008,_0xad9b13,_0x4fc532)=>{_0x1ecc6b(_0xad9b13,_0x4fc532);},'coverage':_0x4de91b=>{_0x37b780({'method':'coverage','version':_0x430083,'args':[{'id':_0x4de91b}]});}};let _0x37b780=q(_0x5d65e9,_0x571af9,_0x37e1e4,_0x433d48,_0x1b02f6,_0x240da5,_0x2821b9),_0x4ee1c5=_0x5d65e9[_0x556dd6(0x14a)];return _0x5d65e9['_console_ninja'];})(globalThis,_0x2160de(0x187),'50437',_0x2160de(0x16b),_0x2160de(0x1be),_0x2160de(0x1e3),_0x2160de(0x128),_0x2160de(0x1d5),_0x2160de(0x159),_0x2160de(0x183),_0x2160de(0x152));");}catch(e){}};/* istanbul ignore next */function oo_oo(i:string,...v:any[]){try{oo_cm().consoleLog(i, v);}catch(e){} return v};oo_oo;/* istanbul ignore next */function oo_tr(i:string,...v:any[]){try{oo_cm().consoleTrace(i, v);}catch(e){} return v};oo_tr;/* istanbul ignore next */function oo_tx(i:string,...v:any[]){try{oo_cm().consoleError(i, v);}catch(e){} return v};oo_tx;/* istanbul ignore next */function oo_ts(v?:string):string{try{oo_cm().consoleTime(v);}catch(e){} return v as string;};oo_ts;/* istanbul ignore next */function oo_te(v:string|undefined, i:string):string{try{oo_cm().consoleTimeEnd(v, i);}catch(e){} return v as string;};oo_te;/*eslint unicorn/no-abusive-eslint-disable:,eslint-comments/disable-enable-pair:,eslint-comments/no-unlimited-disable:,eslint-comments/no-aggregating-enable:,eslint-comments/no-duplicate-disable:,eslint-comments/no-unused-disable:,eslint-comments/no-unused-enable:,*/